import { createAppRequest } from '@/services/request';
import type {
  CompleteEnterpriseExitRequest,
  EnterpriseExitDetail,
  EnterpriseExitListQuery,
  EnterpriseExitListResult,
  ExitCheckResult,
} from './types/enterprise-exit';
import type {
  CreateEnterpriseLibraryItemRequest,
  CreateEnterpriseLibraryResult,
  EnterpriseLibraryBatchInvitationResult,
  EnterpriseLibraryClaimHistory,
  EnterpriseLibraryClaimReview,
  EnterpriseLibraryDetail,
  EnterpriseLibraryExitRecord,
  EnterpriseLibraryImportResult,
  EnterpriseLibraryImportTemplate,
  EnterpriseLibraryInvitation,
  EnterpriseLibraryListItem,
  EnterpriseLibraryListQuery,
  EnterpriseLibrarySpaceOption,
  EnterpriseLibrarySummary,
  ParkEnterpriseType,
  PatchEnterpriseLibraryItemRequest,
} from './types/enterprise-library';
import type {
  AcceptEnterpriseRequestRequest,
  AddEnterpriseRequestFollowUpRequest,
  AssignEnterpriseRequestRequest,
  CompleteEnterpriseRequestRequest,
  EnterpriseRequest,
  EnterpriseRequestListQuery,
  EnterpriseRequestPage,
  RejectEnterpriseRequestRequest,
} from './types/enterprise-request';
import type { EnterpriseRequestStats } from './types/enterprise-request-stats';
import type {
  CreateRequestsFromVisitResponse,
  CreateVisitPlanRequest,
  CreateVisitRecordRequest,
  CreateVisitRequestItem,
  UpdateVisitPlanRequest,
  VisitPlan,
  VisitPlanListQuery,
  VisitRecord,
} from './types/enterprise-visit';
import type {
  FinancingDemand,
  FinancingDemandDetail,
  FinancingDemandListParams,
  FinancingFollowUp,
  FinancingFollowUpInput,
  FinancingFollowUpStage,
  FinancingFollowUpVisibility,
  FinancingMatch,
  FinancingMatchInput,
} from './types/financing';
import type {
  AcceptServiceApplicationRequest,
  ActivityOps,
  ActivityOpsListParams,
  ActivityOpsPageResult,
  ActivityOpsParticipant,
  ActivityOpsRegistration,
  ActivityOpsReview,
  ActivityOpsStatus,
  ActivityOpsWriteRequest,
  ActivityParticipantListResult,
  ActivityRegistrationPageResult,
  ActivityRegistrationStatus,
  AdmissionApplication,
  AdmissionApplicationQuery,
  AnnualIndicator,
  AnnualIndicatorSystem,
  AnnualStatistics,
  AnnualSubmission,
  AnnualTask,
  AnnualTaskDetail,
  Batch3AListParams,
  ChangeServiceApplicationHandlerRequest,
  CompleteServiceApplicationRequest,
  CreateEnterpriseVehicleRequest,
  CreateOperatorParkRequest,
  CreateOperatorParkResponse,
  CreateParkAnnouncementParams,
  CreateParkWorkOrderRecordRequest,
  CreatePartyBuildingParams,
  CreatePolicyParams,
  CreateServiceApplicationRecordRequest,
  Dictionary,
  DictionaryItem,
  DictionaryItemSaveRequest,
  DictionaryListParams,
  DictionarySaveRequest,
  EcosystemResource,
  EcosystemResourceListQuery,
  EcosystemResourceSaveRequest,
  EnergyRecord,
  EnergyRecordQuery,
  EnergyRecordSaveRequest,
  EnergyAccount,
  EnergyBill,
  EnergyBillDetail,
  EnergyBillingCheckResult,
  EnergyBillingCycleConfig,
  EnergyBillingIssueResult,
  EnergyBillStatus,
  EnergyBillingMode,
  EnergyBillingPeriod,
  EnergyRates,
  EnergyRatesPayload,
  EnergySummary,
  EnergyType,
  EnsureDictionaryRequest,
  EnterpriseAggregate,
  EnterpriseAnnualEvent,
  EnterpriseAnnualReport,
  EnterpriseArchiveListQuery,
  EnterpriseContact,
  EnterpriseContactRequest,
  EnterpriseEvaluation,
  EnterpriseEvaluationCreateRequest,
  EnterpriseIntellectualProperty,
  EnterpriseIntellectualPropertyPatchRequest,
  EnterpriseIntellectualPropertyRequest,
  EnterpriseMaterial,
  EnterpriseMaterialHistory,
  EnterpriseMaterialListQuery,
  EnterpriseMaterialRecord,
  EnterpriseMaterialSaveRequest,
  EnterpriseMaterialTemplate,
  EnterpriseMaterialTemplateRequest,
  EnterpriseProduct,
  EnterpriseProductRequest,
  EnterpriseQualification,
  EnterpriseQualificationPatchRequest,
  EnterpriseQualificationRequest,
  EnterpriseSpaceOccupancy,
  EnterpriseVehicle,
  EnterpriseVehicleListParams,
  EnterpriseVehiclePageResult,
  GeneratedNumber,
  GrowthArchive,
  GrowthEnterpriseSummary,
  GrowthEnterpriseSummaryQuery,
  GrowthEvaluation,
  GrowthEvaluationResult,
  GrowthIndicator,
  GrowthManualIndicatorSaveRequest,
  GrowthModel,
  GrowthModelListParams,
  GrowthModelSaveRequest,
  GrowthRecommendation,
  GrowthRecommendationListParams,
  GrowthRecommendationRule,
  GrowthRecommendationRuleListParams,
  GrowthRecommendationRuleSaveRequest,
  GrowthRecommendationStatus,
  GrowthRecommendationTarget,
  GrowthRecommendationTargetType,
  GrowthStage,
  GrowthStageHistory,
  GrowthStageRule,
  GrowthStageRuleSaveRequest,
  GrowthStageSaveRequest,
  IncubationApplication,
  IncubationProgram,
  IncubationProgramSaveRequest,
  InvestmentIntent,
  InvestmentIntentStatusSummary,
  InvestmentProject,
  InvestmentProjectSaveRequest,
  IotDevice,
  IotDeviceQuery,
  IotDeviceSaveRequest,
  IotDeviceStatus,
  ParkAsset,
  ParkAssetQuery,
  ParkAssetReminderItem,
  ParkAssetReminderQuery,
  ParkAssetSaveRequest,
  ParkAssetStatus,
  ParkSupplier,
  ParkSupplierOption,
  ParkSupplierQuery,
  ParkSupplierSaveRequest,
  ParkSupplierStatus,
  Mentor,
  MentorSaveRequest,
  NumberRule,
  NumberRuleListParams,
  NumberRuleSaveRequest,
  OccupancyListQuery,
  OperationContent,
  OperationContentListParams,
  OperationContentPageResult,
  OperationContentSaveRequest,
  OperationLog,
  OperationLogListParams,
  OperatorDashboardResponse,
  OperatorEnterpriseListItem,
  OperatorParkBootstrapStatus,
  PageQuery,
  PageResult,
  Park,
  ParkAccessApplication,
  ParkAccessApplicationListParams,
  ParkAccessApplicationPageResult,
  ParkActivity,
  ParkActivityCreateRequest,
  ParkActivityPageResult,
  ParkActivityQuery,
  ParkActivityRegistrationPageResult,
  ParkActivityRegistrationQuery,
  ParkActivityStatus,
  ParkActivityUpdateRequest,
  ParkAnnouncement,
  ParkAnnouncementAttachment,
  ParkAnnouncementListParams,
  ParkAnnouncementPageResult,
  ParkAnnouncementReadEnterprise,
  ParkAnnouncementReadEnterpriseQuery,
  ParkAnnouncementReadStats,
  ParkEnterpriseRelation,
  ParkEnterpriseRelationQuery,
  ParkFileScene,
  ParkLease,
  ParkLeaseListParams,
  ParkLeaseRequest,
  ParkSpace,
  ParkSpaceCreateRequest,
  ParkSpaceDetail,
  ParkSpaceImportFile,
  ParkSpaceImportResult,
  ParkSpaceStatus,
  ParkSpaceUpdateRequest,
  ParkWorkOrder,
  ParkWorkOrderFileInput,
  ParkWorkOrderListParams,
  ParkWorkOrderPageResult,
  PartyBuilding,
  PartyBuildingListParams,
  PartyBuildingPageResult,
  Policy,
  PolicyListParams,
  PolicyPageResult,
  PortalBanner,
  PortalBannerListParams,
  PortalBannerPageResult,
  PortalBannerSaveRequest,
  PortalBannerStatus,
  PortalColumn,
  PortalColumnListParams,
  PortalColumnListResult,
  PortalColumnPatchRequest,
  PortalColumnSaveRequest,
  PortalContent,
  PortalContentListParams,
  PortalContentPageResult,
  PortalContentSaveRequest,
  PortalContentStatus,
  PortalOperationsOverview,
  PortalPage,
  PortalPageListParams,
  PortalPagePageResult,
  PortalPageSaveRequest,
  PortalRecordStatus,
  PortalSetting,
  PublishManagementListParams,
  PublishManagementOperateRequest,
  PublishManagementPageResult,
  RejectServiceApplicationRequest,
  RequestServiceApplicationSupplementRequest,
  SavePortalSettingRequest,
  ServiceApplication,
  ServiceApplicationListParams,
  ServiceApplicationPageResult,
  ServiceApplicationSummary,
  ServiceCatalog,
  ServiceCatalogQuery,
  ServiceDemand,
  ServiceDemandQuery,
  ServiceFile,
  ServiceFileDownload,
  ServiceFulfillment,
  ServiceFulfillmentQuery,
  ServiceMatter,
  ServiceMatterCategory,
  ServiceMatterCategorySaveRequest,
  ServiceMatterListParams,
  ServiceMatterPageResult,
  ServiceMatterSaveRequest,
  ServiceOrder,
  ServiceOrderQuery,
  ServiceProvider,
  ServiceProviderListParams,
  ServiceProviderSaveRequest,
  ServiceReview,
  ServiceReviewListParams,
  ServiceReviewSummary,
  SpaceChildrenOverview,
  SpaceConfigGroup,
  SpaceConfigItem,
  SpaceConfigItemCreateRequest,
  SpaceConfigItemUpdateRequest,
  SpaceConfigKind,
  SpaceIncubationBinding,
  SpaceIncubationBindingInput,
  SpaceIncubationRoomCapacityResult,
  SpaceListFilters,
  SpaceListQuery,
  SpaceOperationSummary,
  SplitRegistrationUnitsRequest,
  SplitRegistrationUnitsResult,
  UnifiedTodoListResult,
  UnifiedTodoQuery,
  UpdateEnterpriseSpaceOccupancyRequest,
  UpdateOperatorParkRequest,
  UpdateParkAnnouncementParams,
  UpdatePartyBuildingParams,
  UpdatePolicyParams,
  WorkflowTodoDetail,
} from './types/park';
import type {
  ExecuteWorkflowActionRequest,
  SaveWorkflowBindingRequest,
  WorkflowBinding,
  WorkflowLaunchableTemplate,
} from './types/workflow';

const request = createAppRequest();

interface ParkApiEnvelope<T> {
  code: number;
  msg: string;
  data: T;
}

const unwrapParkResponse = <T>(response: ParkApiEnvelope<T>) => response.data;

const requestPark = async <T>(
  url: string,
  options?: Parameters<typeof request>[1],
) => unwrapParkResponse(await request<ParkApiEnvelope<T>>(url, options));

export const parkApi = {
  listTodos: (params?: UnifiedTodoQuery) =>
    requestPark<UnifiedTodoListResult>('/bff/todos', { method: 'GET', params }),
  listVisitPlans: (params?: VisitPlanListQuery) =>
    requestPark<{
      items: VisitPlan[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/visit-plans', { method: 'GET', params }),
  createVisitPlan: (data: CreateVisitPlanRequest) =>
    requestPark<VisitPlan>('/bff/operator/visit-plans', {
      method: 'POST',
      data,
    }),
  getVisitPlan: (uuid: string) =>
    requestPark<VisitPlan>(`/bff/operator/visit-plans/${uuid}`, {
      method: 'GET',
    }),
  updateVisitPlan: (uuid: string, data: UpdateVisitPlanRequest) =>
    requestPark<VisitPlan>(`/bff/operator/visit-plans/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  cancelVisitPlan: (uuid: string) =>
    requestPark<VisitPlan>(`/bff/operator/visit-plans/${uuid}/cancel`, {
      method: 'POST',
    }),
  createVisitRecord: (data: CreateVisitRecordRequest) =>
    requestPark<VisitRecord>('/bff/operator/visit-records', {
      method: 'POST',
      data,
    }),
  getVisitRecord: (uuid: string) =>
    requestPark<VisitRecord>(`/bff/operator/visit-records/${uuid}`, {
      method: 'GET',
    }),
  createRequestsFromVisit: (uuid: string, requests: CreateVisitRequestItem[]) =>
    requestPark<CreateRequestsFromVisitResponse[]>(
      `/bff/operator/visit-records/${uuid}/enterprise-requests`,
      { method: 'POST', data: { requests } },
    ),
  getEnterpriseRequestStats: () =>
    requestPark<EnterpriseRequestStats>(
      '/bff/operator/enterprise-requests/stats',
      { method: 'GET' },
    ),
  listEnterpriseRequests: (params?: EnterpriseRequestListQuery) =>
    requestPark<EnterpriseRequestPage>('/bff/operator/enterprise-requests', {
      method: 'GET',
      params,
    }),
  getEnterpriseRequest: (uuid: string) =>
    requestPark<EnterpriseRequest>(
      `/bff/operator/enterprise-requests/${uuid}`,
      { method: 'GET' },
    ),
  acceptEnterpriseRequest: (
    uuid: string,
    data: AcceptEnterpriseRequestRequest,
  ) =>
    requestPark<EnterpriseRequest>(
      `/bff/operator/enterprise-requests/${uuid}/accept`,
      { method: 'POST', data },
    ),
  rejectEnterpriseRequest: (
    uuid: string,
    data: RejectEnterpriseRequestRequest,
  ) =>
    requestPark<EnterpriseRequest>(
      `/bff/operator/enterprise-requests/${uuid}/reject`,
      { method: 'POST', data },
    ),
  assignEnterpriseRequest: (
    uuid: string,
    data: AssignEnterpriseRequestRequest,
  ) =>
    requestPark<EnterpriseRequest>(
      `/bff/operator/enterprise-requests/${uuid}/assign`,
      { method: 'POST', data },
    ),
  addEnterpriseRequestFollowUp: (
    uuid: string,
    data: AddEnterpriseRequestFollowUpRequest,
  ) =>
    requestPark<EnterpriseRequest>(
      `/bff/operator/enterprise-requests/${uuid}/follow-ups`,
      { method: 'POST', data },
    ),
  completeEnterpriseRequest: (
    uuid: string,
    data: CompleteEnterpriseRequestRequest,
  ) =>
    requestPark<EnterpriseRequest>(
      `/bff/operator/enterprise-requests/${uuid}/complete`,
      { method: 'POST', data },
    ),
  closeEnterpriseRequest: (uuid: string) =>
    requestPark<EnterpriseRequest>(
      `/bff/operator/enterprise-requests/${uuid}/close`,
      { method: 'POST' },
    ),
  getWorkflowTodoDetail: (
    taskUuid: string,
    params: { businessType: string; businessUuid: string },
  ) =>
    requestPark<WorkflowTodoDetail>(`/bff/workflow/tasks/${taskUuid}`, {
      method: 'GET',
      params,
    }),
  listPublishManagement: (params?: PublishManagementListParams) =>
    requestPark<PublishManagementPageResult>(
      '/bff/operator/publish-management',
      {
        method: 'GET',
        params,
      },
    ),
  operatePublishManagement: (data: PublishManagementOperateRequest) =>
    requestPark<void>('/bff/operator/publish-management/operate', {
      method: 'POST',
      data,
    }),
  listOperationLogs: (params?: OperationLogListParams) =>
    requestPark<PageResult<OperationLog>>('/bff/operator/operation-logs', {
      method: 'GET',
      params,
    }),
  getOperationLog: (uuid: string) =>
    requestPark<OperationLog>(`/bff/operator/operation-logs/${uuid}`, {
      method: 'GET',
    }),
  getPortalOperationsOverview: () =>
    requestPark<PortalOperationsOverview>(
      '/bff/operator/portal-operations/overview',
    ),
  listServiceProviders: (params?: ServiceProviderListParams) =>
    requestPark<{
      items: ServiceProvider[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/service-providers', { method: 'GET', params }),
  getServiceProvider: (uuid: string) =>
    requestPark<ServiceProvider>(`/bff/operator/service-providers/${uuid}`, {
      method: 'GET',
    }),
  createServiceProvider: (data: ServiceProviderSaveRequest) =>
    requestPark<ServiceProvider>('/bff/operator/service-providers', {
      method: 'POST',
      data,
    }),
  updateServiceProvider: (uuid: string, data: ServiceProviderSaveRequest) =>
    requestPark<ServiceProvider>(`/bff/operator/service-providers/${uuid}`, {
      method: 'PUT',
      data,
    }),
  publishServiceProvider: (uuid: string) =>
    requestPark<ServiceProvider>(
      `/bff/operator/service-providers/${uuid}/publish`,
      { method: 'POST' },
    ),
  offlineServiceProvider: (uuid: string) =>
    requestPark<ServiceProvider>(
      `/bff/operator/service-providers/${uuid}/offline`,
      { method: 'POST' },
    ),
  deleteServiceProvider: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/service-providers/${uuid}`, {
      method: 'DELETE',
    }),
  listServiceReviews: (params?: ServiceReviewListParams) =>
    requestPark<PageResult<ServiceReview>>('/bff/operator/service-reviews', {
      method: 'GET',
      params,
    }),
  summarizeServiceReviews: (params?: ServiceReviewListParams) =>
    requestPark<ServiceReviewSummary>('/bff/operator/service-reviews/summary', {
      method: 'GET',
      params,
    }),
  getServiceReview: (uuid: string) =>
    requestPark<ServiceReview>(`/bff/operator/service-reviews/${uuid}`, {
      method: 'GET',
    }),
  hideServiceReview: (uuid: string) =>
    requestPark<ServiceReview>(`/bff/operator/service-reviews/${uuid}/hide`, {
      method: 'POST',
    }),
  publishServiceReview: (uuid: string) =>
    requestPark<ServiceReview>(
      `/bff/operator/service-reviews/${uuid}/publish`,
      {
        method: 'POST',
      },
    ),
  listMentors: (params?: {
    keyword?: string;
    status?: Mentor['status'];
    pageNumber?: number;
    pageSize?: number;
  }) =>
    requestPark<{
      items: Mentor[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/mentors', { method: 'GET', params }),
  getMentor: (uuid: string) =>
    requestPark<Mentor>(`/bff/operator/mentors/${uuid}`, { method: 'GET' }),
  createMentor: (data: MentorSaveRequest) =>
    requestPark<Mentor>('/bff/operator/mentors', { method: 'POST', data }),
  updateMentor: (uuid: string, data: MentorSaveRequest) =>
    requestPark<Mentor>(`/bff/operator/mentors/${uuid}`, {
      method: 'PUT',
      data,
    }),
  publishMentor: (uuid: string) =>
    requestPark<Mentor>(`/bff/operator/mentors/${uuid}/publish`, {
      method: 'POST',
    }),
  offlineMentor: (uuid: string) =>
    requestPark<Mentor>(`/bff/operator/mentors/${uuid}/offline`, {
      method: 'POST',
    }),
  deleteMentor: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/mentors/${uuid}`, {
      method: 'DELETE',
    }),
  getEnterpriseAnnualReport: (year: number) =>
    requestPark<EnterpriseAnnualReport>(
      '/bff/operator/enterprise-annual-report',
      { method: 'GET', params: { year } },
    ),
  listEnterpriseAnnualEvents: (
    params: PageQuery & {
      year: number;
      type: 'entry' | 'exit';
      keyword?: string;
    },
  ) =>
    requestPark<PageResult<EnterpriseAnnualEvent>>(
      '/bff/operator/enterprise-annual-report/events',
      { method: 'GET', params },
    ),
  listAnnualIndicators: (params?: PageQuery) =>
    requestPark<PageResult<AnnualIndicator>>(
      '/bff/operator/annual-indicators',
      { method: 'GET', params },
    ),
  listAnnualIndicatorSystems: (params?: PageQuery) =>
    requestPark<PageResult<AnnualIndicatorSystem>>(
      '/bff/operator/annual-indicator-systems',
      { method: 'GET', params },
    ),
  createAnnualIndicatorSystem: (data: Omit<AnnualIndicatorSystem, 'uuid'>) =>
    requestPark<AnnualIndicatorSystem>(
      '/bff/operator/annual-indicator-systems',
      { method: 'POST', data },
    ),
  updateAnnualIndicatorSystem: (
    uuid: string,
    data: Partial<AnnualIndicatorSystem>,
  ) =>
    requestPark<AnnualIndicatorSystem>(
      `/bff/operator/annual-indicator-systems/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteAnnualIndicatorSystem: (uuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/annual-indicator-systems/${uuid}`,
      { method: 'DELETE' },
    ),
  createAnnualIndicator: (data: Omit<AnnualIndicator, 'uuid'>) =>
    requestPark<AnnualIndicator>('/bff/operator/annual-indicators', {
      method: 'POST',
      data,
    }),
  updateAnnualIndicator: (uuid: string, data: Partial<AnnualIndicator>) =>
    requestPark<AnnualIndicator>(`/bff/operator/annual-indicators/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  deleteAnnualIndicator: (uuid: string) =>
    requestPark(`/bff/operator/annual-indicators/${uuid}`, {
      method: 'DELETE',
    }),
  listAnnualTasks: (params?: PageQuery & { year?: number }) =>
    requestPark<PageResult<AnnualTask>>(
      '/bff/operator/annual-collection-tasks',
      { method: 'GET', params },
    ),
  getAnnualTask: (uuid: string) =>
    requestPark<AnnualTaskDetail>(
      `/bff/operator/annual-collection-tasks/${uuid}`,
      { method: 'GET' },
    ),
  createAnnualTask: (
    data: Omit<AnnualTask, 'uuid' | 'status'> & {
      indicatorUuids: string[];
      enterpriseTenantUuids?: string[];
    },
  ) =>
    requestPark<AnnualTaskDetail>('/bff/operator/annual-collection-tasks', {
      method: 'POST',
      data,
    }),
  updateAnnualTask: (
    uuid: string,
    data: Omit<AnnualTask, 'uuid' | 'status'> & {
      indicatorUuids: string[];
      enterpriseTenantUuids?: string[];
    },
  ) =>
    requestPark<AnnualTaskDetail>(
      `/bff/operator/annual-collection-tasks/${uuid}`,
      { method: 'PATCH', data },
    ),
  changeAnnualTaskStatus: (uuid: string, status: string) =>
    requestPark<AnnualTask>(
      `/bff/operator/annual-collection-tasks/${uuid}/status`,
      { method: 'POST', data: { status } },
    ),
  listAnnualSubmissions: (taskUuid: string, params?: PageQuery) =>
    requestPark<PageResult<AnnualSubmission>>(
      `/bff/operator/annual-collection-tasks/${taskUuid}/submissions`,
      { method: 'GET', params },
    ),
  getAnnualSubmission: (taskUuid: string, uuid: string) =>
    requestPark<AnnualSubmission>(
      `/bff/operator/annual-collection-tasks/${taskUuid}/submissions/${uuid}`,
      { method: 'GET' },
    ),
  getTaskEnterpriseEvaluation: (
    taskUuid: string,
    enterpriseTenantUuid: string,
  ) =>
    requestPark<AnnualTaskDetail & { submission: AnnualSubmission }>(
      `/bff/operator/annual-collection-tasks/${taskUuid}/enterprises/${enterpriseTenantUuid}/evaluation`,
      { method: 'GET' },
    ),
  scoreAnnualSubmission: (
    taskUuid: string,
    uuid: string,
    data: {
      values: Array<{ indicatorUuid: string; score: number; comment?: string }>;
      comment?: string;
    },
  ) =>
    requestPark<AnnualSubmission>(
      `/bff/operator/annual-collection-tasks/${taskUuid}/submissions/${uuid}/score`,
      { method: 'POST', data },
    ),
  returnAnnualSubmission: (taskUuid: string, uuid: string, reason: string) =>
    requestPark<AnnualSubmission>(
      `/bff/operator/annual-collection-tasks/${taskUuid}/submissions/${uuid}/return`,
      { method: 'POST', data: { reason } },
    ),
  confirmAnnualSubmission: (taskUuid: string, uuid: string) =>
    requestPark<AnnualSubmission>(
      `/bff/operator/annual-collection-tasks/${taskUuid}/submissions/${uuid}/confirm`,
      { method: 'POST' },
    ),
  getAnnualStatistics: (year: number) =>
    requestPark<AnnualStatistics>('/bff/operator/annual-statistics', {
      method: 'GET',
      params: { year },
    }),
  listEvaluationReports: (params?: PageQuery) =>
    requestPark<PageResult<any>>('/bff/operator/evaluation/reports', {
      method: 'GET',
      params,
    }),
  getEvaluationOverview: () =>
    requestPark<{
      taskCount: number;
      inProgressCount: number;
      completedCount: number;
      enterpriseCount: number;
      recentTasks: AnnualTask[];
    }>('/bff/operator/evaluation/overview', { method: 'GET' }),
  getOperatorDashboard: () =>
    requestPark<OperatorDashboardResponse>('/bff/operator/dashboard', {
      method: 'GET',
    }),
  listAccessibleParks: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<Park[]>>('/bff/operator/parks', {
        method: 'GET',
        skipParkContext: true,
      }),
    ),
  getBootstrapStatus: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<OperatorParkBootstrapStatus>>(
        '/bff/operator/parks/bootstrap-status',
        { method: 'GET', skipParkContext: true },
      ),
    ),
  uploadFile: async (file: File, scene: ParkFileScene) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scene', scene);
    return unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceFile>>('/bff/files', {
        method: 'POST',
        data: formData,
        requestType: 'form',
        skipParkContext: scene === 'park_cover',
      }),
    );
  },
  getFile: async (fileUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceFileDownload>>(
        `/bff/files/${fileUuid}`,
        {
          method: 'GET',
        },
      ),
    ),
  createOperatorPark: async (data: CreateOperatorParkRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<CreateOperatorParkResponse>>(
        '/bff/operator/parks',
        {
          method: 'POST',
          data,
          skipParkContext: true,
        },
      ),
    ),
  getOperatorCurrentPark: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<Park>>('/bff/operator/parks/current', {
        method: 'GET',
      }),
    ),
  updateOperatorCurrentPark: async (data: UpdateOperatorParkRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<Park>>('/bff/operator/parks/current', {
        method: 'PATCH',
        data,
      }),
    ),
  getCurrentPark: () =>
    requestPark<Park>('/bff/park/current', { method: 'GET' }),
  listEnterprises: async (params?: ParkEnterpriseRelationQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<OperatorEnterpriseListItem>>>(
        '/bff/operator/enterprises',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  getEnterprise: async (enterpriseTenantUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnterpriseAggregate>>(
        `/bff/operator/enterprises/${enterpriseTenantUuid}`,
        { method: 'GET' },
      ),
    ),
  listEnterpriseMaterialTemplates: (params?: EnterpriseMaterialListQuery) =>
    requestPark<PageResult<EnterpriseMaterialTemplate>>(
      '/bff/operator/enterprise-material-templates',
      { method: 'GET', params },
    ),
  getEnterpriseMaterialTemplate: (templateUuid: string) =>
    requestPark<EnterpriseMaterialTemplate>(
      `/bff/operator/enterprise-material-templates/${templateUuid}`,
      { method: 'GET' },
    ),
  createEnterpriseMaterialTemplate: (data: EnterpriseMaterialTemplateRequest) =>
    requestPark<EnterpriseMaterialTemplate>(
      '/bff/operator/enterprise-material-templates',
      { method: 'POST', data },
    ),
  updateEnterpriseMaterialTemplate: (
    templateUuid: string,
    data: Partial<EnterpriseMaterialTemplateRequest>,
  ) =>
    requestPark<EnterpriseMaterialTemplate>(
      `/bff/operator/enterprise-material-templates/${templateUuid}`,
      { method: 'PATCH', data },
    ),
  updateEnterpriseMaterialTemplateStatus: (
    templateUuid: string,
    status: EnterpriseMaterialTemplate['status'],
  ) =>
    requestPark<EnterpriseMaterialTemplate>(
      `/bff/operator/enterprise-material-templates/${templateUuid}/status`,
      { method: 'POST', data: { status } },
    ),
  listEnterpriseMaterials: (enterpriseTenantUuid: string) =>
    requestPark<EnterpriseMaterialRecord[]>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/materials`,
      { method: 'GET' },
    ),
  listLocalEnterpriseMaterials: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseMaterialRecord[]>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/materials`,
      { method: 'GET' },
    ),
  saveEnterpriseMaterial: (
    enterpriseTenantUuid: string,
    templateUuid: string,
    data: EnterpriseMaterialSaveRequest,
  ) =>
    requestPark<EnterpriseMaterial>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/materials/${templateUuid}`,
      { method: 'POST', data },
    ),
  saveLocalEnterpriseMaterial: (
    parkEnterpriseUuid: string,
    templateUuid: string,
    data: EnterpriseMaterialSaveRequest,
  ) =>
    requestPark<EnterpriseMaterial>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/materials/${templateUuid}`,
      { method: 'POST', data },
    ),
  deleteEnterpriseMaterial: (
    enterpriseTenantUuid: string,
    templateUuid: string,
  ) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/materials/${templateUuid}`,
      { method: 'DELETE' },
    ),
  deleteLocalEnterpriseMaterial: (
    parkEnterpriseUuid: string,
    templateUuid: string,
  ) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/materials/${templateUuid}`,
      { method: 'DELETE' },
    ),
  listEnterpriseMaterialHistory: (
    enterpriseTenantUuid: string,
    templateUuid: string,
  ) =>
    requestPark<EnterpriseMaterialHistory[]>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/materials/${templateUuid}/history`,
      { method: 'GET' },
    ),
  listEnterpriseContacts: (
    enterpriseTenantUuid: string,
    params?: EnterpriseArchiveListQuery,
  ) =>
    requestPark<PageResult<EnterpriseContact>>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/contacts`,
      { method: 'GET', params },
    ),
  getEnterpriseContact: (enterpriseTenantUuid: string, uuid: string) =>
    requestPark<EnterpriseContact>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/contacts/${uuid}`,
      { method: 'GET' },
    ),
  createEnterpriseContact: (
    enterpriseTenantUuid: string,
    data: EnterpriseContactRequest,
  ) =>
    requestPark<EnterpriseContact>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/contacts`,
      { method: 'POST', data },
    ),
  updateEnterpriseContact: (
    enterpriseTenantUuid: string,
    uuid: string,
    data: Partial<EnterpriseContactRequest>,
  ) =>
    requestPark<EnterpriseContact>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/contacts/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteEnterpriseContact: (enterpriseTenantUuid: string, uuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/contacts/${uuid}`,
      { method: 'DELETE' },
    ),
  listEnterpriseProducts: (
    enterpriseTenantUuid: string,
    params?: EnterpriseArchiveListQuery,
  ) =>
    requestPark<PageResult<EnterpriseProduct>>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/products`,
      { method: 'GET', params },
    ),
  getEnterpriseProduct: (enterpriseTenantUuid: string, uuid: string) =>
    requestPark<EnterpriseProduct>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/products/${uuid}`,
      { method: 'GET' },
    ),
  createEnterpriseProduct: (
    enterpriseTenantUuid: string,
    data: EnterpriseProductRequest,
  ) =>
    requestPark<EnterpriseProduct>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/products`,
      { method: 'POST', data },
    ),
  updateEnterpriseProduct: (
    enterpriseTenantUuid: string,
    uuid: string,
    data: Partial<EnterpriseProductRequest>,
  ) =>
    requestPark<EnterpriseProduct>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/products/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteEnterpriseProduct: (enterpriseTenantUuid: string, uuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/products/${uuid}`,
      { method: 'DELETE' },
    ),
  listEnterpriseQualifications: (
    enterpriseTenantUuid: string,
    params?: EnterpriseArchiveListQuery,
  ) =>
    requestPark<PageResult<EnterpriseQualification>>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/qualifications`,
      { method: 'GET', params },
    ),
  listLocalEnterpriseQualifications: (
    parkEnterpriseUuid: string,
    params?: EnterpriseArchiveListQuery,
  ) =>
    requestPark<PageResult<EnterpriseQualification>>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/qualifications`,
      { method: 'GET', params },
    ),
  getEnterpriseQualification: (enterpriseTenantUuid: string, uuid: string) =>
    requestPark<EnterpriseQualification>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/qualifications/${uuid}`,
      { method: 'GET' },
    ),
  getLocalEnterpriseQualification: (parkEnterpriseUuid: string, uuid: string) =>
    requestPark<EnterpriseQualification>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/qualifications/${uuid}`,
      { method: 'GET' },
    ),
  createEnterpriseQualification: (
    enterpriseTenantUuid: string,
    data: EnterpriseQualificationRequest,
  ) =>
    requestPark<EnterpriseQualification>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/qualifications`,
      { method: 'POST', data },
    ),
  createLocalEnterpriseQualification: (
    parkEnterpriseUuid: string,
    data: EnterpriseQualificationRequest,
  ) =>
    requestPark<EnterpriseQualification>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/qualifications`,
      { method: 'POST', data },
    ),
  updateEnterpriseQualification: (
    enterpriseTenantUuid: string,
    uuid: string,
    data: EnterpriseQualificationPatchRequest,
  ) =>
    requestPark<EnterpriseQualification>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/qualifications/${uuid}`,
      { method: 'PATCH', data },
    ),
  updateLocalEnterpriseQualification: (
    parkEnterpriseUuid: string,
    uuid: string,
    data: EnterpriseQualificationPatchRequest,
  ) =>
    requestPark<EnterpriseQualification>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/qualifications/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteEnterpriseQualification: (enterpriseTenantUuid: string, uuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/qualifications/${uuid}`,
      { method: 'DELETE' },
    ),
  deleteLocalEnterpriseQualification: (
    parkEnterpriseUuid: string,
    uuid: string,
  ) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/qualifications/${uuid}`,
      { method: 'DELETE' },
    ),
  listEnterpriseIntellectualProperties: (
    enterpriseTenantUuid: string,
    params?: EnterpriseArchiveListQuery,
  ) =>
    requestPark<PageResult<EnterpriseIntellectualProperty>>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/intellectual-properties`,
      { method: 'GET', params },
    ),
  listLocalEnterpriseIntellectualProperties: (
    parkEnterpriseUuid: string,
    params?: EnterpriseArchiveListQuery,
  ) =>
    requestPark<PageResult<EnterpriseIntellectualProperty>>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/intellectual-properties`,
      { method: 'GET', params },
    ),
  getEnterpriseIntellectualProperty: (
    enterpriseTenantUuid: string,
    uuid: string,
  ) =>
    requestPark<EnterpriseIntellectualProperty>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/intellectual-properties/${uuid}`,
      { method: 'GET' },
    ),
  getLocalEnterpriseIntellectualProperty: (
    parkEnterpriseUuid: string,
    uuid: string,
  ) =>
    requestPark<EnterpriseIntellectualProperty>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/intellectual-properties/${uuid}`,
      { method: 'GET' },
    ),
  createEnterpriseIntellectualProperty: (
    enterpriseTenantUuid: string,
    data: EnterpriseIntellectualPropertyRequest,
  ) =>
    requestPark<EnterpriseIntellectualProperty>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/intellectual-properties`,
      { method: 'POST', data },
    ),
  createLocalEnterpriseIntellectualProperty: (
    parkEnterpriseUuid: string,
    data: EnterpriseIntellectualPropertyRequest,
  ) =>
    requestPark<EnterpriseIntellectualProperty>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/intellectual-properties`,
      { method: 'POST', data },
    ),
  updateEnterpriseIntellectualProperty: (
    enterpriseTenantUuid: string,
    uuid: string,
    data: EnterpriseIntellectualPropertyPatchRequest,
  ) =>
    requestPark<EnterpriseIntellectualProperty>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/intellectual-properties/${uuid}`,
      { method: 'PATCH', data },
    ),
  updateLocalEnterpriseIntellectualProperty: (
    parkEnterpriseUuid: string,
    uuid: string,
    data: EnterpriseIntellectualPropertyPatchRequest,
  ) =>
    requestPark<EnterpriseIntellectualProperty>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/intellectual-properties/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteEnterpriseIntellectualProperty: (
    enterpriseTenantUuid: string,
    uuid: string,
  ) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/intellectual-properties/${uuid}`,
      { method: 'DELETE' },
    ),
  deleteLocalEnterpriseIntellectualProperty: (
    parkEnterpriseUuid: string,
    uuid: string,
  ) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/intellectual-properties/${uuid}`,
      { method: 'DELETE' },
    ),
  listEnterpriseEvaluations: (
    enterpriseTenantUuid: string,
    params?: PageQuery,
  ) =>
    requestPark<PageResult<EnterpriseEvaluation>>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/evaluations`,
      { method: 'GET', params },
    ),
  getEnterpriseEvaluation: (
    enterpriseTenantUuid: string,
    evaluationUuid: string,
  ) =>
    requestPark<EnterpriseEvaluation>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/evaluations/${evaluationUuid}`,
      { method: 'GET' },
    ),
  createEnterpriseEvaluation: (
    enterpriseTenantUuid: string,
    data: EnterpriseEvaluationCreateRequest,
  ) =>
    requestPark<EnterpriseEvaluation>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/evaluations`,
      { method: 'POST', data },
    ),
  updateEnterpriseProfile: (
    enterpriseTenantUuid: string,
    data: Record<string, unknown>,
  ) =>
    requestPark(`/bff/operator/enterprises/${enterpriseTenantUuid}/profile`, {
      method: 'PATCH',
      data,
    }),
  updateEnterpriseRelation: (
    enterpriseTenantUuid: string,
    data: Record<string, unknown>,
  ) =>
    requestPark(`/bff/operator/enterprises/${enterpriseTenantUuid}/relation`, {
      method: 'PATCH',
      data,
    }),
  suspendEnterprise: (enterpriseTenantUuid: string) =>
    requestPark(`/bff/operator/enterprises/${enterpriseTenantUuid}/suspend`, {
      method: 'POST',
    }),
  resumeEnterprise: (enterpriseTenantUuid: string) =>
    requestPark(`/bff/operator/enterprises/${enterpriseTenantUuid}/resume`, {
      method: 'POST',
    }),
  graduateEnterprise: (enterpriseTenantUuid: string) =>
    requestPark(`/bff/operator/enterprises/${enterpriseTenantUuid}/graduate`, {
      method: 'POST',
    }),
  exitEnterprise: (enterpriseTenantUuid: string) =>
    requestPark(`/bff/operator/enterprises/${enterpriseTenantUuid}/exit`, {
      method: 'POST',
    }),

  listRelations: (params?: ParkEnterpriseRelationQuery) =>
    requestPark<PageResult<ParkEnterpriseRelation>>(
      '/bff/park/current/relations',
      {
        method: 'GET',
        params,
      },
    ),
  createRelation: (data: Partial<ParkEnterpriseRelation>) =>
    requestPark<ParkEnterpriseRelation>('/bff/park/current/relations', {
      method: 'POST',
      data,
    }),
  updateRelation: (
    relationUuid: string,
    data: Partial<ParkEnterpriseRelation>,
  ) =>
    requestPark<ParkEnterpriseRelation>(
      `/bff/park/current/relations/${relationUuid}`,
      {
        method: 'PATCH',
        data,
      },
    ),

  listApplications: async (params?: AdmissionApplicationQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<AdmissionApplication>>>(
        '/bff/park/current/admission-applications',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  getApplication: async (applicationUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<AdmissionApplication>>(
        `/bff/park/current/admission-applications/${applicationUuid}`,
        { method: 'GET' },
      ),
    ),
  createApplication: (data: Partial<AdmissionApplication>) =>
    requestPark<AdmissionApplication>(
      '/bff/park/current/admission-applications',
      { method: 'POST', data },
    ),
  updateApplication: async (
    applicationUuid: string,
    data: Partial<AdmissionApplication>,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<AdmissionApplication>>(
        `/bff/park/current/admission-applications/${applicationUuid}`,
        {
          method: 'PATCH',
          data,
        },
      ),
    ),

  listDemands: (params?: ServiceDemandQuery) =>
    requestPark<PageResult<ServiceDemand>>(
      '/bff/park/current/service-demands',
      { method: 'GET', params },
    ),
  getDemand: (demandUuid: string) =>
    requestPark<ServiceDemand>(
      `/bff/park/current/service-demands/${demandUuid}`,
      { method: 'GET' },
    ),
  createDemand: (data: Partial<ServiceDemand>) =>
    requestPark<ServiceDemand>('/bff/park/current/service-demands', {
      method: 'POST',
      data,
    }),
  updateDemand: (demandUuid: string, data: Partial<ServiceDemand>) =>
    requestPark<ServiceDemand>(
      `/bff/park/current/service-demands/${demandUuid}`,
      { method: 'PATCH', data },
    ),
  convertDemandToOrder: (demandUuid: string, data: Partial<ServiceOrder>) =>
    requestPark<{ serviceOrder: ServiceOrder; serviceDemand: ServiceDemand }>(
      `/bff/park/current/service-demands/${demandUuid}/convert-order`,
      { method: 'POST', data },
    ),

  listCatalogs: (params?: ServiceCatalogQuery) =>
    requestPark<PageResult<ServiceCatalog>>(
      '/bff/park/current/service-catalog',
      { method: 'GET', params },
    ),
  getCatalog: (catalogUuid: string) =>
    requestPark<ServiceCatalog>(
      `/bff/park/current/service-catalog/${catalogUuid}`,
      { method: 'GET' },
    ),
  createCatalog: (data: Partial<ServiceCatalog>) =>
    requestPark<ServiceCatalog>('/bff/park/current/service-catalog', {
      method: 'POST',
      data,
    }),
  updateCatalog: (catalogUuid: string, data: Partial<ServiceCatalog>) =>
    requestPark<ServiceCatalog>(
      `/bff/park/current/service-catalog/${catalogUuid}`,
      { method: 'PATCH', data },
    ),

  listOrders: (params?: ServiceOrderQuery) =>
    requestPark<PageResult<ServiceOrder>>('/bff/park/current/service-orders', {
      method: 'GET',
      params,
    }),
  getOrder: (orderUuid: string) =>
    requestPark<ServiceOrder>(`/bff/park/current/service-orders/${orderUuid}`, {
      method: 'GET',
    }),
  createOrder: (data: Partial<ServiceOrder>) =>
    requestPark<ServiceOrder>('/bff/park/current/service-orders', {
      method: 'POST',
      data,
    }),
  updateOrder: (orderUuid: string, data: Partial<ServiceOrder>) =>
    requestPark<ServiceOrder>(`/bff/park/current/service-orders/${orderUuid}`, {
      method: 'PATCH',
      data,
    }),

  listFulfillments: (orderUuid: string, params?: ServiceFulfillmentQuery) =>
    requestPark<PageResult<ServiceFulfillment>>(
      `/bff/park/current/service-orders/${orderUuid}/fulfillments`,
      {
        method: 'GET',
        params,
      },
    ),
  listOperatorFulfillmentsByQuery: (
    params?: ServiceFulfillmentQuery & { enterpriseTenantUuid?: string },
  ) =>
    requestPark<PageResult<ServiceFulfillment>>(
      '/bff/park/current/service-fulfillments',
      { method: 'GET', params },
    ),
  createFulfillment: (orderUuid: string, data: Partial<ServiceFulfillment>) =>
    requestPark<ServiceFulfillment>(
      `/bff/park/current/service-orders/${orderUuid}/fulfillments`,
      {
        method: 'POST',
        data,
      },
    ),
  updateFulfillment: (
    fulfillmentUuid: string,
    data: Partial<ServiceFulfillment>,
  ) =>
    requestPark<ServiceFulfillment>(
      `/bff/park/current/service-fulfillments/${fulfillmentUuid}`,
      {
        method: 'PATCH',
        data,
      },
    ),

  listActivities: async (params?: ParkActivityQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkActivityPageResult>>(
        '/bff/operator/activities',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  listActivityOps: async (params?: ActivityOpsListParams) =>
    requestPark<ActivityOpsPageResult>('/bff/operator/activity-ops', {
      method: 'GET',
      params,
    }),
  getActivityOps: async (uuid: string) =>
    requestPark<ActivityOps>(`/bff/operator/activity-ops/${uuid}`, {
      method: 'GET',
    }),
  listActivityOpsRegistrations: async (uuid: string) =>
    requestPark<ActivityOpsRegistration[]>(
      `/bff/operator/activity-ops/${uuid}/registrations`,
      { method: 'GET' },
    ),
  listActivityRegistrationManagement: async (params?: {
    activityUuid?: string;
    status?: ActivityRegistrationStatus;
    keyword?: string;
    page?: number;
    pageSize?: number;
  }) =>
    requestPark<ActivityRegistrationPageResult>(
      '/bff/operator/activity-ops/management/registrations',
      { method: 'GET', params },
    ),
  getActivityRegistrationDetail: async (
    activityUuid: string,
    registrationUuid: string,
  ) =>
    requestPark<ActivityOpsRegistration>(
      `/bff/operator/activity-ops/${activityUuid}/registrations/${registrationUuid}/detail`,
      { method: 'GET' },
    ),
  reviewActivityRegistration: async (
    activityUuid: string,
    registrationUuid: string,
    data: { status: 'APPROVED' | 'REJECTED'; rejectReason?: string },
  ) =>
    requestPark<ActivityOpsRegistration>(
      `/bff/operator/activity-ops/${activityUuid}/registrations/${registrationUuid}/review`,
      { method: 'POST', data },
    ),
  batchReviewActivityRegistrations: async (
    activityUuid: string,
    uuids: string[],
  ) =>
    requestPark<ActivityOpsRegistration[]>(
      `/bff/operator/activity-ops/${activityUuid}/registrations/batch-review`,
      { method: 'POST', data: { uuids } },
    ),
  listActivityOpsParticipants: async (activityUuid: string) =>
    requestPark<ActivityParticipantListResult>(
      `/bff/operator/activity-ops/${activityUuid}/participants`,
      { method: 'GET' },
    ),
  checkinActivityParticipant: async (
    activityUuid: string,
    participantUuid: string,
  ) =>
    requestPark<ActivityOpsParticipant>(
      `/bff/operator/activity-ops/${activityUuid}/participants/${participantUuid}/checkin`,
      { method: 'POST' },
    ),
  cancelActivityParticipantCheckin: async (
    activityUuid: string,
    participantUuid: string,
  ) =>
    requestPark<ActivityOpsParticipant>(
      `/bff/operator/activity-ops/${activityUuid}/participants/${participantUuid}/cancel-checkin`,
      { method: 'POST' },
    ),
  batchCheckinActivityParticipants: async (
    activityUuid: string,
    uuids: string[],
  ) =>
    requestPark<ActivityOpsParticipant[]>(
      `/bff/operator/activity-ops/${activityUuid}/participants/batch-checkin`,
      { method: 'POST', data: { uuids } },
    ),
  batchCancelActivityParticipantCheckins: async (
    activityUuid: string,
    uuids: string[],
  ) =>
    requestPark<ActivityOpsParticipant[]>(
      `/bff/operator/activity-ops/${activityUuid}/participants/batch-cancel-checkin`,
      { method: 'POST', data: { uuids } },
    ),
  createActivityOps: async (data: ActivityOpsWriteRequest) =>
    requestPark<ActivityOps>('/bff/operator/activity-ops', {
      method: 'POST',
      data,
    }),
  updateActivityOps: async (
    uuid: string,
    data: Partial<ActivityOpsWriteRequest>,
  ) =>
    requestPark<ActivityOps>(`/bff/operator/activity-ops/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  submitActivityOpsPublish: async (uuid: string) =>
    requestPark<ActivityOps & { approvalMode?: 'WORKFLOW' | 'AUTO_APPROVED' }>(
      `/bff/operator/activity-ops/${uuid}/submit-publish`,
      { method: 'POST' },
    ),
  changeActivityOpsStatus: async (uuid: string, status: ActivityOpsStatus) =>
    requestPark<ActivityOps>(`/bff/operator/activity-ops/${uuid}/status`, {
      method: 'POST',
      data: { status },
    }),
  getActivityOpsReview: async (uuid: string) =>
    requestPark<ActivityOpsReview | null>(
      `/bff/operator/activity-ops/${uuid}/review`,
      { method: 'GET' },
    ),
  saveActivityOpsReview: async (
    uuid: string,
    data: Partial<ActivityOpsReview>,
  ) =>
    requestPark<ActivityOpsReview>(
      `/bff/operator/activity-ops/${uuid}/review`,
      { method: 'PUT', data },
    ),
  getActivity: async (activityUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkActivity>>(
        `/bff/operator/activities/${activityUuid}`,
        {
          method: 'GET',
        },
      ),
    ),
  createActivity: async (data: ParkActivityCreateRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkActivity>>('/bff/operator/activities', {
        method: 'POST',
        data,
      }),
    ),
  updateActivity: async (
    activityUuid: string,
    data: ParkActivityUpdateRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkActivity>>(
        `/bff/operator/activities/${activityUuid}`,
        {
          method: 'PATCH',
          data,
        },
      ),
    ),
  deleteActivity: async (activityUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/activities/${activityUuid}`,
        { method: 'DELETE' },
      ),
    ),
  updateActivityStatus: async (
    activityUuid: string,
    status: ParkActivityStatus,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkActivity>>(
        `/bff/operator/activities/${activityUuid}/status`,
        {
          method: 'POST',
          data: { status },
        },
      ),
    ),
  listActivityRegistrations: async (
    activityUuid: string,
    params?: ParkActivityRegistrationQuery,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkActivityRegistrationPageResult>>(
        `/bff/operator/activities/${activityUuid}/registrations`,
        { method: 'GET', params },
      ),
    ),

  listPolicies: async (params?: PolicyListParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PolicyPageResult>>(
        '/bff/operator/policies',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  getPolicy: async (policyUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<Policy>>(
        `/bff/operator/policies/${policyUuid}`,
        { method: 'GET' },
      ),
    ),
  createPolicy: async (data: CreatePolicyParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<Policy>>('/bff/operator/policies', {
        method: 'POST',
        data,
      }),
    ),
  updatePolicy: async (policyUuid: string, data: UpdatePolicyParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<Policy>>(
        `/bff/operator/policies/${policyUuid}`,
        { method: 'PATCH', data },
      ),
    ),
  deletePolicy: async (policyUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/policies/${policyUuid}`,
        { method: 'DELETE' },
      ),
    ),
  updatePolicyStatus: async (policyUuid: string, status: Policy['status']) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<Policy>>(
        `/bff/operator/policies/${policyUuid}/status`,
        {
          method: 'POST',
          data: { status },
        },
      ),
    ),

  listPartyBuildings: async (params?: PartyBuildingListParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PartyBuildingPageResult>>(
        '/bff/operator/party-buildings',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  getPartyBuilding: async (partyBuildingUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PartyBuilding>>(
        `/bff/operator/party-buildings/${partyBuildingUuid}`,
        {
          method: 'GET',
        },
      ),
    ),
  createPartyBuilding: async (data: CreatePartyBuildingParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PartyBuilding>>(
        '/bff/operator/party-buildings',
        {
          method: 'POST',
          data,
        },
      ),
    ),
  updatePartyBuilding: async (
    partyBuildingUuid: string,
    data: UpdatePartyBuildingParams,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PartyBuilding>>(
        `/bff/operator/party-buildings/${partyBuildingUuid}`,
        {
          method: 'PATCH',
          data,
        },
      ),
    ),
  deletePartyBuilding: async (partyBuildingUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/party-buildings/${partyBuildingUuid}`,
        {
          method: 'DELETE',
        },
      ),
    ),
  updatePartyBuildingStatus: async (
    partyBuildingUuid: string,
    status: PartyBuilding['status'],
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PartyBuilding>>(
        `/bff/operator/party-buildings/${partyBuildingUuid}/status`,
        {
          method: 'POST',
          data: { status },
        },
      ),
    ),

  listOperationContents: (params?: OperationContentListParams) =>
    requestPark<OperationContentPageResult>(
      '/bff/operator/operation-contents',
      { method: 'GET', params },
    ),
  getOperationContent: (uuid: string) =>
    requestPark<OperationContent>(`/bff/operator/operation-contents/${uuid}`, {
      method: 'GET',
    }),
  createOperationContent: (data: OperationContentSaveRequest) =>
    requestPark<OperationContent>('/bff/operator/operation-contents', {
      method: 'POST',
      data,
    }),
  updateOperationContent: (
    uuid: string,
    data: Partial<OperationContentSaveRequest>,
  ) =>
    requestPark<OperationContent>(`/bff/operator/operation-contents/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  updateOperationContentStatus: (
    uuid: string,
    status: OperationContent['status'],
  ) =>
    requestPark<OperationContent>(
      `/bff/operator/operation-contents/${uuid}/status`,
      { method: 'POST', data: { status } },
    ),
  deleteOperationContent: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/operation-contents/${uuid}`, {
      method: 'DELETE',
    }),

  listAnnouncements: async (params?: ParkAnnouncementListParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncementPageResult>>(
        '/bff/operator/announcements',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  getAnnouncement: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncement>>(
        `/bff/operator/announcements/${uuid}`,
        {
          method: 'GET',
        },
      ),
    ),
  createAnnouncement: async (data: CreateParkAnnouncementParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncement>>(
        '/bff/operator/announcements',
        {
          method: 'POST',
          data,
        },
      ),
    ),
  updateAnnouncement: async (
    uuid: string,
    data: UpdateParkAnnouncementParams,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncement>>(
        `/bff/operator/announcements/${uuid}`,
        {
          method: 'PATCH',
          data,
        },
      ),
    ),
  publishAnnouncement: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncement>>(
        `/bff/operator/announcements/${uuid}/publish`,
        {
          method: 'POST',
        },
      ),
    ),
  withdrawAnnouncement: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncement>>(
        `/bff/operator/announcements/${uuid}/withdraw`,
        {
          method: 'POST',
        },
      ),
    ),
  pinAnnouncement: async (uuid: string, pinned: boolean) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncement>>(
        `/bff/operator/announcements/${uuid}/pin`,
        {
          method: 'POST',
          data: { pinned },
        },
      ),
    ),
  deleteAnnouncement: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/announcements/${uuid}`,
        {
          method: 'DELETE',
        },
      ),
    ),
  deleteAnnouncementAttachment: async (uuid: string, attachmentUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/announcements/${uuid}/attachments/${attachmentUuid}`,
        { method: 'DELETE' },
      ),
    ),
  sortAnnouncementAttachments: async (
    uuid: string,
    items: Array<{ uuid: string; sort: number }>,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncementAttachment[]>>(
        `/bff/operator/announcements/${uuid}/attachments/sort`,
        { method: 'POST', data: { items } },
      ),
    ),
  getAnnouncementReadStats: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAnnouncementReadStats>>(
        `/bff/operator/announcements/${uuid}/read-stats`,
        { method: 'GET' },
      ),
    ),
  listAnnouncementReadEnterprises: async (
    uuid: string,
    params?: ParkAnnouncementReadEnterpriseQuery,
  ) =>
    unwrapParkResponse(
      await request<
        ParkApiEnvelope<PageResult<ParkAnnouncementReadEnterprise>>
      >(`/bff/operator/announcements/${uuid}/read-enterprises`, {
        method: 'GET',
        params,
      }),
    ),

  listEcosystemResources: (params?: EcosystemResourceListQuery) =>
    requestPark<PageResult<EcosystemResource>>(
      '/bff/operator/ecosystem-resources',
      {
        method: 'GET',
        params,
      },
    ),
  getEcosystemResource: (uuid: string) =>
    requestPark<EcosystemResource>(
      `/bff/operator/ecosystem-resources/${uuid}`,
      { method: 'GET' },
    ),
  createEcosystemResource: (data: EcosystemResourceSaveRequest) =>
    requestPark<EcosystemResource>('/bff/operator/ecosystem-resources', {
      method: 'POST',
      data,
    }),
  updateEcosystemResource: (
    uuid: string,
    data: Partial<EcosystemResourceSaveRequest>,
  ) =>
    requestPark<EcosystemResource>(
      `/bff/operator/ecosystem-resources/${uuid}`,
      {
        method: 'PATCH',
        data,
      },
    ),
  updateEcosystemResourceStatus: (
    uuid: string,
    status: EcosystemResource['status'],
  ) =>
    requestPark<EcosystemResource>(
      `/bff/operator/ecosystem-resources/${uuid}/status`,
      {
        method: 'POST',
        data: { status },
      },
    ),
  deleteEcosystemResource: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/ecosystem-resources/${uuid}`, {
      method: 'DELETE',
    }),
  listFinancingDemands: (params?: FinancingDemandListParams) =>
    requestPark<{
      items: FinancingDemand[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/financing-demands', { method: 'GET', params }),
  getFinancingDemand: (uuid: string) =>
    requestPark<FinancingDemandDetail>(
      `/bff/operator/financing-demands/${uuid}`,
      { method: 'GET' },
    ),
  startFinancingDemand: (uuid: string) =>
    requestPark<FinancingDemand>(
      `/bff/operator/financing-demands/${uuid}/start-processing`,
      { method: 'POST' },
    ),
  rejectFinancingDemand: (uuid: string) =>
    requestPark<FinancingDemand>(
      `/bff/operator/financing-demands/${uuid}/reject`,
      { method: 'POST' },
    ),
  closeFinancingDemand: (uuid: string) =>
    requestPark<FinancingDemand>(
      `/bff/operator/financing-demands/${uuid}/close`,
      { method: 'POST' },
    ),
  listFinancingMatches: (demandUuid: string) =>
    requestPark<FinancingMatch[]>(
      `/bff/operator/financing-demands/${demandUuid}/matches`,
      { method: 'GET' },
    ),
  createFinancingMatch: (demandUuid: string, data: FinancingMatchInput) =>
    requestPark<FinancingMatch>(
      `/bff/operator/financing-demands/${demandUuid}/matches`,
      { method: 'POST', data },
    ),
  updateFinancingMatch: (uuid: string, data: FinancingMatchInput) =>
    requestPark<FinancingMatch>(`/bff/operator/financing-matches/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  addFinancingFollowUp: (uuid: string, data: FinancingFollowUpInput) =>
    requestPark<FinancingFollowUp>(
      `/bff/operator/financing-matches/${uuid}/follow-ups`,
      { method: 'POST', data },
    ),
  updateFinancingStage: (
    uuid: string,
    data: {
      stage: FinancingFollowUpStage;
      content?: string;
      nextFollowUpAt?: string;
      visibility?: FinancingFollowUpVisibility;
    },
  ) =>
    requestPark<FinancingFollowUp>(
      `/bff/operator/financing-matches/${uuid}/stage`,
      { method: 'POST', data },
    ),
  endFinancingMatch: (
    uuid: string,
    matchStatus: 'success' | 'failed' | 'terminated',
    content?: string,
  ) =>
    requestPark<FinancingMatch>(`/bff/operator/financing-matches/${uuid}/end`, {
      method: 'POST',
      data: { matchStatus, content },
    }),

  listSpaces: async (params?: SpaceListQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<ParkSpace>>>(
        '/bff/operator/spaces',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  listAllSpacesLegacy: async (
    params?: Omit<SpaceListQuery, 'pageNumber' | 'pageSize'>,
  ) => {
    const pageSize = 100;
    const items: ParkSpace[] = [];
    let pageNumber = 1;
    let total = 0;
    do {
      const result = await parkApi.listSpaces({
        ...params,
        pageNumber,
        pageSize,
      });
      items.push(...result.items);
      total = result.total;
      if (!result.items.length) break;
      pageNumber += 1;
    } while (items.length < total);
    return {
      items,
      pageNumber: 1,
      pageSize: items.length || pageSize,
      total,
    };
  },
  getSpaceSummary: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<SpaceOperationSummary>>(
        '/bff/operator/spaces/summary',
        {
          method: 'GET',
        },
      ),
    ),
  getSpaceChildrenOverview: async (parentUuid?: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<SpaceChildrenOverview>>(
        '/bff/operator/spaces/children-overview',
        {
          method: 'GET',
          params: parentUuid ? { parentUuid } : undefined,
        },
      ),
    ),
  getSpace: async (
    uuid: string,
    params?: { pageNumber?: number; pageSize?: number },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkSpaceDetail>>(
        `/bff/operator/spaces/${uuid}`,
        {
          method: 'GET',
          params,
        },
      ),
    ),
  createSpace: async (data: ParkSpaceCreateRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkSpace>>('/bff/operator/spaces', {
        method: 'POST',
        data,
      }),
    ),
  updateSpace: async (uuid: string, data: ParkSpaceUpdateRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkSpace>>(
        `/bff/operator/spaces/${uuid}`,
        {
          method: 'PATCH',
          data,
        },
      ),
    ),
  updateSpaceStatus: async (uuid: string, status: ParkSpaceStatus) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkSpace>>(
        `/bff/operator/spaces/${uuid}/status`,
        {
          method: 'POST',
          data: { status },
        },
      ),
    ),
  splitRegistrationUnits: async (
    uuid: string,
    data: SplitRegistrationUnitsRequest,
  ) =>
    requestPark<SplitRegistrationUnitsResult>(
      `/bff/operator/spaces/${uuid}/registration-units/split`,
      { method: 'POST', data },
    ),
  /** 下载当前房源类型的批量导入模板 */
  downloadSpacesImportTemplate: (inventoryScope?: 'physical' | 'virtual') =>
    requestPark<ParkSpaceImportFile>('/bff/operator/spaces/import/template', {
      method: 'GET',
      params: inventoryScope ? { inventoryScope } : undefined,
    }),
  /** 房源批量导入：multipart/form-data，字段名 file */
  importSpaces: (file: File, inventoryScope?: 'physical' | 'virtual') => {
    const data = new FormData();
    data.append('file', file);
    return requestPark<ParkSpaceImportResult>('/bff/operator/spaces/import', {
      method: 'POST',
      data,
      params: inventoryScope ? { inventoryScope } : undefined,
    });
  },
  /** 按当前筛选导出台账；不传筛选即导出全部 */
  exportSpaces: (params?: SpaceListFilters) =>
    requestPark<ParkSpaceImportFile>('/bff/operator/spaces/export', {
      method: 'GET',
      params,
    }),
  listSpaceOccupancies: async (params?: OccupancyListQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<EnterpriseSpaceOccupancy>>>(
        '/bff/operator/space-occupancies',
        { method: 'GET', params },
      ),
    ),
  updateSpaceOccupancy: async (
    uuid: string,
    data: UpdateEnterpriseSpaceOccupancyRequest,
  ) =>
    requestPark<EnterpriseSpaceOccupancy>(
      `/bff/operator/space-occupancies/${uuid}`,
      { method: 'PATCH', data },
    ),
  listSpaceIncubationBindings: (params?: PageQuery & { spaceUuid?: string }) =>
    requestPark<PageResult<SpaceIncubationBinding>>(
      '/bff/operator/space-incubation-bindings',
      { method: 'GET', params },
    ),
  createSpaceIncubationBinding: (data: SpaceIncubationBindingInput) =>
    requestPark<SpaceIncubationBinding>(
      '/bff/operator/space-incubation-bindings',
      { method: 'POST', data },
    ),
  deleteSpaceIncubationBinding: (uuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/space-incubation-bindings/${encodeURIComponent(uuid)}`,
      { method: 'DELETE' },
    ),
  /** 下载孵化档案批量导入模板（Base64，前端落地成 xlsx） */
  downloadSpaceIncubationBindingsImportTemplate: () =>
    requestPark<ParkSpaceImportFile>(
      '/bff/operator/space-incubation-bindings/import/template',
      { method: 'GET' },
    ),
  /** 孵化档案批量导入：multipart/form-data，字段名 file */
  importSpaceIncubationBindings: (file: File) => {
    const data = new FormData();
    data.append('file', file);
    return requestPark<ParkSpaceImportResult>(
      '/bff/operator/space-incubation-bindings/import',
      { method: 'POST', data },
    );
  },
  /** 导出孵化档案（该页无筛选条件，导出全部） */
  exportSpaceIncubationBindings: () =>
    requestPark<ParkSpaceImportFile>(
      '/bff/operator/space-incubation-bindings/export',
      { method: 'GET' },
    ),
  /** 各房间的孵化容量及当前园区最低注册面积 */
  listSpaceIncubationRoomCapacities: () =>
    requestPark<SpaceIncubationRoomCapacityResult>(
      '/bff/operator/space-incubation-bindings/room-capacities',
      { method: 'GET' },
    ),
  updateSpaceIncubationRegistrationArea: (areaPerEnterprise: number) =>
    requestPark<{ areaPerEnterprise: number }>(
      '/bff/operator/space-incubation-bindings/registration-area',
      { method: 'PATCH', data: { areaPerEnterprise } },
    ),
  listAllSpaceOccupanciesLegacy: async (
    params?: Omit<OccupancyListQuery, 'pageNumber' | 'pageSize'>,
  ) => {
    const pageSize = 100;
    const items: EnterpriseSpaceOccupancy[] = [];
    let pageNumber = 1;
    let total = 0;
    do {
      const result = await parkApi.listSpaceOccupancies({
        ...params,
        pageNumber,
        pageSize,
      });
      items.push(...result.items);
      total = result.total;
      if (!result.items.length) break;
      pageNumber += 1;
    } while (items.length < total);
    return {
      items,
      pageNumber: 1,
      pageSize: items.length || pageSize,
      total,
    };
  },
  createSpaceOccupancy: async (
    spaceUuid: string,
    data: {
      enterpriseTenantUuid?: string;
      parkEnterpriseUuid?: string;
      usageType?: "actual" | "registration";
      startDate: string;
      expectedEndDate?: string | null;
      remark?: string;
    },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnterpriseSpaceOccupancy>>(
        `/bff/operator/spaces/${spaceUuid}/occupancies`,
        { method: 'POST', data },
      ),
    ),
  endSpaceOccupancy: async (uuid: string, data?: { endDate?: string }) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnterpriseSpaceOccupancy>>(
        `/bff/operator/space-occupancies/${uuid}/end`,
        { method: 'POST', data },
      ),
    ),

  listEnergyRecords: async (params?: EnergyRecordQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<EnergyRecord>>>(
        '/bff/operator/energy-records',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  getEnergyRecord: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyRecord>>(
        `/bff/operator/energy-records/${uuid}`,
        { method: 'GET' },
      ),
    ),
  createEnergyRecord: async (data: EnergyRecordSaveRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyRecord>>(
        '/bff/operator/energy-records',
        { method: 'POST', data },
      ),
    ),
  updateEnergyRecord: async (
    uuid: string,
    data: Partial<EnergyRecordSaveRequest>,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyRecord>>(
        `/bff/operator/energy-records/${uuid}`,
        { method: 'PATCH', data },
      ),
    ),
  deleteEnergyRecord: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/energy-records/${uuid}`,
        { method: 'DELETE' },
      ),
    ),
  getEnergySummary: async (
    params?: Omit<EnergyRecordQuery, 'pageNumber' | 'pageSize'>,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergySummary>>(
        '/bff/operator/energy-records/summary',
        { method: 'GET', params },
      ),
    ),
  /** 抄表基线：同表最近一条记录的本期读数，作为本次上期读数 */
  getEnergyMeterBaseline: async (params: {
    meterCode: string;
    energyType: EnergyType;
    recordedAt?: string;
  }) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ meterCode: string; previousReading: number }>>(
        '/bff/operator/energy-records/baseline',
        { method: 'GET', params },
      ),
    ),
  getEnergyRates: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyRatesPayload>>('/bff/operator/energy-billing/rates', { method: 'GET' }),
    ),
  saveEnergyRates: async (data: {
    rates?: EnergyRates;
    billingCycle?: EnergyBillingCycleConfig;
  }) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ rates: EnergyRates; billingCycle: EnergyBillingCycleConfig }>>(
        '/bff/operator/energy-billing/rates',
        { method: 'PATCH', data },
      ),
    ),
  getEnergyBillingPeriod: async (date?: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyBillingPeriod>>('/bff/operator/energy-billing/period', {
        method: 'GET',
        params: date ? { date } : undefined,
      }),
    ),
  listEnergyAccounts: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyAccount[]>>('/bff/operator/energy-billing/accounts', { method: 'GET' }),
    ),
  saveEnergyAccount: async (
    enterpriseTenantUuid: string,
    data: { mode: EnergyBillingMode; warningAmount: number; warningUsage?: number | null },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyAccount>>(`/bff/operator/energy-billing/accounts/${enterpriseTenantUuid}`, { method: 'PATCH', data }),
    ),
  listEnergyMeters: async (spaceUuid?: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').EnergyMeter[]>>(
        '/bff/operator/energy-meters',
        { method: 'GET', params: spaceUuid ? { spaceUuid } : undefined },
      ),
    ),
  createEnergyMeter: async (data: import('./types/park').EnergyMeterSaveRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').EnergyMeter>>(
        '/bff/operator/energy-meters',
        { method: 'POST', data },
      ),
    ),
  updateEnergyMeter: async (
    uuid: string,
    data: Partial<import('./types/park').EnergyMeterSaveRequest>,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').EnergyMeter>>(
        `/bff/operator/energy-meters/${uuid}`,
        { method: 'PATCH', data },
      ),
    ),
  deleteEnergyMeter: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(`/bff/operator/energy-meters/${uuid}`, {
        method: 'DELETE',
      }),
    ),
  /** 手工补生成某账期的待抄表任务（定时器漏跑时用）。 */
  generateEnergyMeterTasks: async (period?: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ created: number; skipped: number }>>(
        '/bff/operator/energy-meters/generate-tasks',
        { method: 'POST', params: period ? { period } : undefined },
      ),
    ),
  /** 抄表员填读数：待抄表任务转为已确认。 */
  fillEnergyReading: async (recordUuid: string, currentReading: number) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').EnergyRecord>>(
        `/bff/operator/energy-records/${recordUuid}/reading`,
        { method: 'POST', data: { currentReading } },
      ),
    ),

  addEnergyPayment: async (
    enterpriseTenantUuid: string,
    data: {
      amount: number;
      remark?: string;
      kind?: 'recharge' | 'payment';
      /** 水费与电费的预存必须分开记，不能互相抵扣。 */
      energyType: string;
    },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<unknown>>(`/bff/operator/energy-billing/accounts/${enterpriseTenantUuid}/payments`, { method: 'POST', data }),
    ),
  checkEnergyBills: async (data: {
    period?: string;
    date?: string;
    periodStart?: string;
    periodEnd?: string;
  }) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyBillingCheckResult>>('/bff/operator/energy-billing/bills/check', {
        method: 'POST',
        data,
      }),
    ),
  createEnergyBills: async (data: {
    period?: string;
    date?: string;
    periodStart?: string;
    periodEnd?: string;
  }) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyBillingIssueResult>>('/bff/operator/energy-billing/bills', {
        method: 'POST',
        data,
      }),
    ),
  listEnergyBills: async (params?: {
    period?: string;
    enterpriseTenantUuid?: string;
    status?: EnergyBillStatus;
    pageNumber?: number;
    pageSize?: number;
  }) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<EnergyBill>>>('/bff/operator/energy-billing/bills', {
        method: 'GET',
        params,
      }),
    ),
  getEnergyBill: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<EnergyBillDetail>>(`/bff/operator/energy-billing/bills/${uuid}`, {
        method: 'GET',
      }),
    ),
  reverseEnergyBill: async (uuid: string, data?: { reason?: string }) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<unknown>>(`/bff/operator/energy-billing/bills/${uuid}/reverse`, {
        method: 'POST',
        data,
      }),
    ),

  listIotDevices: async (params?: IotDeviceQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<IotDevice>>>(
        '/bff/operator/iot-devices',
        { method: 'GET', params },
      ),
    ),
  getIotDevice: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<IotDevice>>(
        `/bff/operator/iot-devices/${uuid}`,
        { method: 'GET' },
      ),
    ),
  createIotDevice: async (data: IotDeviceSaveRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<IotDevice>>('/bff/operator/iot-devices', {
        method: 'POST',
        data,
      }),
    ),
  updateIotDevice: async (uuid: string, data: Partial<IotDeviceSaveRequest>) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<IotDevice>>(
        `/bff/operator/iot-devices/${uuid}`,
        { method: 'PATCH', data },
      ),
    ),
  updateIotDeviceStatus: async (uuid: string, status: IotDeviceStatus) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<IotDevice>>(
        `/bff/operator/iot-devices/${uuid}/status`,
        { method: 'POST', data: { status } },
      ),
    ),
  deleteIotDevice: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/iot-devices/${uuid}`,
        { method: 'DELETE' },
      ),
    ),
  listLeases: async (params?: ParkLeaseListParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<ParkLease>>>(
        '/bff/operator/leases',
        {
          method: 'GET',
          params,
        },
      ),
    ),
  listEnterpriseLeases: async (
    enterpriseTenantUuid: string,
    params?: Omit<ParkLeaseListParams, 'enterpriseTenantUuid'>,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<ParkLease>>>(
        `/bff/operator/enterprises/${enterpriseTenantUuid}/leases`,
        {
          method: 'GET',
          params,
        },
      ),
    ),
  getLease: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkLease>>(
        `/bff/operator/leases/${uuid}`,
        { method: 'GET' },
      ),
    ),
  createLease: async (data: ParkLeaseRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkLease>>('/bff/operator/leases', {
        method: 'POST',
        data,
      }),
    ),
  updateLease: async (uuid: string, data: Partial<ParkLeaseRequest>) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkLease>>(
        `/bff/operator/leases/${uuid}`,
        { method: 'PATCH', data },
      ),
    ),
  deleteLease: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/leases/${uuid}`,
        { method: 'DELETE' },
      ),
    ),
  activateLease: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkLease>>(
        `/bff/operator/leases/${uuid}/activate`,
        { method: 'POST' },
      ),
    ),
  createLeaseContract: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkLease>>(
        `/bff/operator/leases/${uuid}/contract`,
        { method: 'POST' },
      ),
    ),
  syncLeaseContract: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkLease>>(
        `/bff/operator/leases/${uuid}/contract-sync`,
        { method: 'POST' },
      ),
    ),
  getLeaseSettlementPlans: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<any>>(
        `/bff/operator/leases/${uuid}/settlement-plans`,
        { method: 'GET' },
      ),
    ),
  syncLeaseFinance: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkLease>>(
        `/bff/operator/leases/${uuid}/finance-sync`,
        { method: 'POST' },
      ),
    ),
  terminateLease: async (uuid: string, data?: { endDate?: string }) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkLease>>(
        `/bff/operator/leases/${uuid}/terminate`,
        { method: 'POST', data },
      ),
    ),

  listLeasePlans: async (
    params?: import('./types/park').ParkLeasePlanListParams,
  ) =>
    unwrapParkResponse(
      await request<
        ParkApiEnvelope<PageResult<import('./types/park').ParkLeasePlan>>
      >('/bff/operator/lease-plans', { method: 'GET', params }),
    ),
  getLeasePlan: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeasePlan>>(
        `/bff/operator/lease-plans/${uuid}`,
        { method: 'GET' },
      ),
    ),
  createLeasePlan: async (
    data: import('./types/park').CreateParkLeasePlanRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeasePlan>>(
        '/bff/operator/lease-plans',
        { method: 'POST', data },
      ),
    ),
  patchLeasePlan: async (
    uuid: string,
    data: import('./types/park').PatchParkLeasePlanRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeasePlan>>(
        `/bff/operator/lease-plans/${uuid}`,
        { method: 'PATCH', data },
      ),
    ),
  deleteLeasePlan: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/lease-plans/${uuid}`,
        { method: 'DELETE' },
      ),
    ),
  submitLeasePlan: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeasePlan>>(
        `/bff/operator/lease-plans/${uuid}/submit`,
        { method: 'POST' },
      ),
    ),
  cancelLeasePlan: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeasePlan>>(
        `/bff/operator/lease-plans/${uuid}/cancel`,
        { method: 'POST' },
      ),
    ),
  listLeaseRenewals: async (
    params?: import('./types/park').ParkLeaseRenewalListParams,
  ) =>
    unwrapParkResponse(
      await request<
        ParkApiEnvelope<PageResult<import('./types/park').ParkLeaseRenewal>>
      >('/bff/operator/lease-renewals', { method: 'GET', params }),
    ),
  getLeaseRenewal: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeaseRenewal>>(
        `/bff/operator/lease-renewals/${uuid}`,
        { method: 'GET' },
      ),
    ),
  createLeaseRenewal: async (
    data: import('./types/park').ParkLeaseRenewalRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeaseRenewal>>(
        '/bff/operator/lease-renewals',
        { method: 'POST', data },
      ),
    ),
  patchLeaseRenewal: async (
    uuid: string,
    data: Partial<import('./types/park').ParkLeaseRenewalRequest>,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeaseRenewal>>(
        `/bff/operator/lease-renewals/${uuid}`,
        { method: 'PATCH', data },
      ),
    ),
  submitLeaseRenewal: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeaseRenewal>>(
        `/bff/operator/lease-renewals/${uuid}/submit`,
        { method: 'POST' },
      ),
    ),
  cancelLeaseRenewal: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeaseRenewal>>(
        `/bff/operator/lease-renewals/${uuid}/cancel`,
        { method: 'POST' },
      ),
    ),
  prepareLeaseRenewal: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeaseRenewal>>(
        `/bff/operator/lease-renewals/${uuid}/prepare`,
        { method: 'POST' },
      ),
    ),
  syncLeaseRenewalContract: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeaseRenewal>>(
        `/bff/operator/lease-renewals/${uuid}/contract-sync`,
        { method: 'POST' },
      ),
    ),
  executeLeaseRenewal: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeaseRenewal>>(
        `/bff/operator/lease-renewals/${uuid}/execute`,
        { method: 'POST' },
      ),
    ),
  listLeaseTerminations: async (
    params?: import('./types/park').ParkLeaseTerminationListParams,
  ) =>
    unwrapParkResponse(
      await request<
        ParkApiEnvelope<PageResult<import('./types/park').ParkLeaseTermination>>
      >('/bff/operator/lease-terminations', { method: 'GET', params }),
    ),
  getLeaseTermination: async (uuid: string) =>
    unwrapParkResponse(
      await request<
        ParkApiEnvelope<import('./types/park').ParkLeaseTermination>
      >(`/bff/operator/lease-terminations/${uuid}`, { method: 'GET' }),
    ),
  executeLeaseTermination: async (uuid: string) =>
    unwrapParkResponse(
      await request<
        ParkApiEnvelope<import('./types/park').ParkLeaseTermination>
      >(`/bff/operator/lease-terminations/${uuid}/execute`, { method: 'POST' }),
    ),
  startLeaseHandover: async (
    uuid: string,
    data?: { plannedDate?: string; remark?: string },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<unknown>>(
        `/bff/operator/lease-handovers/${uuid}/start`,
        { method: 'POST', data },
      ),
    ),
  completeLeaseHandover: async (
    uuid: string,
    data?: { actualDate?: string; remark?: string },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<unknown>>(
        `/bff/operator/lease-handovers/${uuid}/complete`,
        { method: 'POST', data },
      ),
    ),
  listEnterpriseLeasePlans: async (
    params?: import('./types/park').ParkLeasePlanListParams,
  ) =>
    unwrapParkResponse(
      await request<
        ParkApiEnvelope<PageResult<import('./types/park').ParkLeasePlan>>
      >('/bff/enterprise/current/lease-plans', { method: 'GET', params }),
    ),
  getEnterpriseLeasePlan: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<import('./types/park').ParkLeasePlan>>(
        `/bff/enterprise/current/lease-plans/${uuid}`,
        { method: 'GET' },
      ),
    ),

  listEnterpriseRelations: (params?: ParkEnterpriseRelationQuery) =>
    requestPark<PageResult<ParkEnterpriseRelation>>(
      '/bff/enterprise/current/relations',
      {
        method: 'GET',
        params,
      },
    ),
  listEnterpriseApplications: (params?: AdmissionApplicationQuery) =>
    requestPark<PageResult<AdmissionApplication>>(
      '/bff/enterprise/current/admission-applications',
      {
        method: 'GET',
        params,
      },
    ),
  createEnterpriseApplication: (data: Partial<AdmissionApplication>) =>
    requestPark<AdmissionApplication>(
      '/bff/enterprise/current/admission-applications',
      { method: 'POST', data },
    ),
  withdrawEnterpriseApplication: (applicationUuid: string) =>
    requestPark<AdmissionApplication>(
      `/bff/enterprise/current/admission-applications/${applicationUuid}/withdraw`,
      {
        method: 'POST',
      },
    ),
  listEnterpriseDemands: (params?: ServiceDemandQuery) =>
    requestPark<PageResult<ServiceDemand>>(
      '/bff/enterprise/current/service-demands',
      { method: 'GET', params },
    ),
  createEnterpriseDemand: (data: Partial<ServiceDemand>) =>
    requestPark<ServiceDemand>('/bff/enterprise/current/service-demands', {
      method: 'POST',
      data,
    }),
  updateEnterpriseDemand: (demandUuid: string, data: Partial<ServiceDemand>) =>
    requestPark<ServiceDemand>(
      `/bff/enterprise/current/service-demands/${demandUuid}`,
      { method: 'PATCH', data },
    ),
  listEnterpriseCatalogs: (params?: ServiceCatalogQuery) =>
    requestPark<PageResult<ServiceCatalog>>(
      '/bff/enterprise/current/service-catalog',
      {
        method: 'GET',
        params,
      },
    ),
  listEnterpriseOrders: (params?: ServiceOrderQuery) =>
    requestPark<PageResult<ServiceOrder>>(
      '/bff/enterprise/current/service-orders',
      { method: 'GET', params },
    ),
  getEnterpriseOrder: (orderUuid: string) =>
    requestPark<ServiceOrder>(
      `/bff/enterprise/current/service-orders/${orderUuid}`,
      { method: 'GET' },
    ),
  listEnterpriseFulfillments: (orderUuid: string) =>
    requestPark<PageResult<ServiceFulfillment>>(
      `/bff/enterprise/current/service-orders/${orderUuid}/fulfillments`,
      {
        method: 'GET',
      },
    ),
  acceptEnterpriseFulfillment: (
    fulfillmentUuid: string,
    data?: Partial<ServiceFulfillment>,
  ) =>
    requestPark<ServiceFulfillment>(
      `/bff/enterprise/current/service-fulfillments/${fulfillmentUuid}/accept`,
      {
        method: 'POST',
        data,
      },
    ),

  downloadServiceFile: async (fileUuid: string) => parkApi.getFile(fileUuid),

  listServiceMatterCategories: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ items: ServiceMatterCategory[] }>>(
        '/bff/operator/service-matter-categories',
        { method: 'GET' },
      ),
    ),
  createServiceMatterCategory: async (data: ServiceMatterCategorySaveRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatterCategory>>(
        '/bff/operator/service-matter-categories',
        { method: 'POST', data },
      ),
    ),
  updateServiceMatterCategory: async (
    uuid: string,
    data: ServiceMatterCategorySaveRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatterCategory>>(
        `/bff/operator/service-matter-categories/${uuid}`,
        { method: 'PUT', data },
      ),
    ),
  deleteServiceMatterCategory: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/service-matter-categories/${uuid}`,
        { method: 'DELETE' },
      ),
    ),

  listServiceMatters: async (params?: ServiceMatterListParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatterPageResult>>(
        '/bff/operator/service-matters',
        { method: 'GET', params },
      ),
    ),
  getServiceMatter: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatter>>(
        `/bff/operator/service-matters/${uuid}`,
        { method: 'GET' },
      ),
    ),
  createServiceMatter: async (data: ServiceMatterSaveRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatter>>(
        '/bff/operator/service-matters',
        {
          method: 'POST',
          data,
        },
      ),
    ),
  updateServiceMatter: async (uuid: string, data: ServiceMatterSaveRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatter>>(
        `/bff/operator/service-matters/${uuid}`,
        { method: 'PUT', data },
      ),
    ),
  deleteServiceMatter: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(
        `/bff/operator/service-matters/${uuid}`,
        { method: 'DELETE' },
      ),
    ),
  publishServiceMatter: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatter>>(
        `/bff/operator/service-matters/${uuid}/publish`,
        { method: 'POST' },
      ),
    ),
  offlineServiceMatter: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatter>>(
        `/bff/operator/service-matters/${uuid}/offline`,
        { method: 'POST' },
      ),
    ),
  setServiceMatterApplicationEnabled: async (
    uuid: string,
    applicationEnabled: boolean,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceMatter>>(
        `/bff/operator/service-matters/${uuid}/application-enabled`,
        { method: 'POST', data: { applicationEnabled } },
      ),
    ),

  listServiceApplications: async (params?: ServiceApplicationListParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplicationPageResult>>(
        '/bff/operator/service-applications',
        { method: 'GET', params },
      ),
    ),
  getServiceApplicationSummary: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplicationSummary>>(
        '/bff/operator/service-applications/summary',
        { method: 'GET' },
      ),
    ),
  getServiceApplication: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplication>>(
        `/bff/operator/service-applications/${uuid}`,
        { method: 'GET' },
      ),
    ),
  acceptServiceApplication: async (
    uuid: string,
    data: AcceptServiceApplicationRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplication>>(
        `/bff/operator/service-applications/${uuid}/accept`,
        { method: 'POST', data },
      ),
    ),
  startServiceApplication: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplication>>(
        `/bff/operator/service-applications/${uuid}/start`,
        { method: 'POST' },
      ),
    ),
  changeServiceApplicationHandler: async (
    uuid: string,
    data: ChangeServiceApplicationHandlerRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplication>>(
        `/bff/operator/service-applications/${uuid}/change-handler`,
        { method: 'POST', data },
      ),
    ),
  createServiceApplicationRecord: async (
    uuid: string,
    data: CreateServiceApplicationRecordRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplication>>(
        `/bff/operator/service-applications/${uuid}/records`,
        { method: 'POST', data },
      ),
    ),
  requestServiceApplicationSupplement: async (
    uuid: string,
    data: RequestServiceApplicationSupplementRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplication>>(
        `/bff/operator/service-applications/${uuid}/request-supplement`,
        { method: 'POST', data },
      ),
    ),
  completeServiceApplication: async (
    uuid: string,
    data: CompleteServiceApplicationRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplication>>(
        `/bff/operator/service-applications/${uuid}/complete`,
        { method: 'POST', data },
      ),
    ),
  rejectServiceApplication: async (
    uuid: string,
    data: RejectServiceApplicationRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ServiceApplication>>(
        `/bff/operator/service-applications/${uuid}/reject`,
        { method: 'POST', data },
      ),
    ),
  getPropertyWorkOrderDashboard: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<any>>(
        '/bff/operator/property-work-orders/dashboard',
        { method: 'GET' },
      ),
    ),
  getPropertyWorkOrderTrend: async (days = 7) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<any>>(
        '/bff/operator/property-work-orders/stats/trend',
        { method: 'GET', params: { days } },
      ),
    ),
  listPropertyWorkOrderSlaRules: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<any[]>>(
        '/bff/operator/property-work-orders/sla-rules',
        { method: 'GET' },
      ),
    ),
  createPropertyWorkOrderSlaRule: async (data: any) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<any>>(
        '/bff/operator/property-work-orders/sla-rules',
        { method: 'POST', data },
      ),
    ),
  updatePropertyWorkOrderSlaRule: async (uuid: string, data: any) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<any>>(
        `/bff/operator/property-work-orders/sla-rules/${uuid}`,
        { method: 'PATCH', data },
      ),
    ),
  togglePropertyWorkOrderSlaRule: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<any>>(
        `/bff/operator/property-work-orders/sla-rules/${uuid}/toggle`,
        { method: 'POST' },
      ),
    ),
  deletePropertyWorkOrderSlaRule: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<boolean>>(
        `/bff/operator/property-work-orders/sla-rules/${uuid}`,
        { method: 'DELETE' },
      ),
    ),
  listWorkOrders: async (params?: ParkWorkOrderListParams) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrderPageResult>>(
        '/bff/operator/work-orders',
        { method: 'GET', params },
      ),
    ),
  getWorkOrder: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}`,
        { method: 'GET' },
      ),
    ),
  acceptWorkOrder: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/accept`,
        { method: 'POST' },
      ),
    ),
  acceptPropertyWorkOrder: async (uuid: string, remark?: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/accept`,
        {
          method: 'POST',
          data: remark ? { remark } : undefined,
        },
      ),
    ),
  changeWorkOrderHandler: async (uuid: string, handlerMemberUuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/handler`,
        { method: 'POST', data: { handlerMemberUuid } },
      ),
    ),
  assignPropertyWorkOrder: async (
    uuid: string,
    data: {
      handlerMemberUuid: string;
      teamUuid?: string;
      teamName?: string;
      remark?: string;
    },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/assign`,
        {
          method: 'POST',
          data,
        },
      ),
    ),
  reassignPropertyWorkOrder: async (
    uuid: string,
    data: {
      handlerMemberUuid: string;
      teamUuid?: string;
      teamName?: string;
      remark?: string;
    },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/reassign`,
        {
          method: 'POST',
          data,
        },
      ),
    ),
  receiveWorkOrder: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/receive`,
        { method: 'POST' },
      ),
    ),
  rejectWorkOrder: async (uuid: string, reason: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/reject`,
        {
          method: 'POST',
          data: { reason },
        },
      ),
    ),
  startWorkOrder: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/start`,
        { method: 'POST' },
      ),
    ),
  addWorkOrderRecord: async (
    uuid: string,
    data: CreateParkWorkOrderRecordRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/records`,
        { method: 'POST', data },
      ),
    ),
  requestWorkOrderSupplement: async (
    uuid: string,
    data: CreateParkWorkOrderRecordRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/request-supplement`,
        { method: 'POST', data },
      ),
    ),
  completeWorkOrder: async (
    uuid: string,
    data: CreateParkWorkOrderRecordRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/complete`,
        { method: 'POST', data },
      ),
    ),
  closePropertyWorkOrder: async (uuid: string, remark?: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/property/work-orders/${uuid}/close`,
        { method: 'POST', data: remark ? { remark } : {} },
      ),
    ),
  addPropertyProcessRecord: async (
    uuid: string,
    data: CreateParkWorkOrderRecordRequest,
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/process-records`,
        { method: 'POST', data },
      ),
    ),
  transferPropertyWorkOrder: async (
    uuid: string,
    data: {
      handlerMemberUuid: string;
      reason: string;
      teamUuid?: string;
      teamName?: string;
    },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/transfer`,
        { method: 'POST', data },
      ),
    ),
  assistPropertyWorkOrder: async (
    uuid: string,
    data: { assistMemberUuid: string; reason: string },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/assist`,
        { method: 'POST', data },
      ),
    ),
  suspendPropertyWorkOrder: async (uuid: string, reason: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/suspend`,
        { method: 'POST', data: { reason } },
      ),
    ),
  resumePropertyWorkOrder: async (uuid: string, remark?: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/resume`,
        { method: 'POST', data: remark ? { remark } : {} },
      ),
    ),
  submitPropertyWorkOrderCompletion: async (
    uuid: string,
    data: {
      resultDescription: string;
      hasFee?: boolean;
      amount?: string;
      feeRemark?: string;
      attachments?: ParkWorkOrderFileInput[];
    },
  ) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkWorkOrder>>(
        `/bff/operator/work-orders/${uuid}/submit-completion`,
        { method: 'POST', data },
      ),
    ),
  listAccessApplications: (params?: ParkAccessApplicationListParams) =>
    requestPark<ParkAccessApplicationPageResult>(
      '/bff/operator/access-applications',
      { method: 'GET', params },
    ),
  getAccessApplication: (uuid: string) =>
    requestPark<ParkAccessApplication>(
      `/bff/operator/access-applications/${uuid}`,
      { method: 'GET' },
    ),
  approveAccessApplication: (uuid: string, remark?: string) =>
    requestPark<ParkAccessApplication>(
      `/bff/operator/access-applications/${uuid}/approve`,
      { method: 'POST', data: { remark } },
    ),
  rejectAccessApplication: (uuid: string, remark: string) =>
    requestPark<ParkAccessApplication>(
      `/bff/operator/access-applications/${uuid}/reject`,
      { method: 'POST', data: { remark } },
    ),
  completeAccessApplication: (uuid: string) =>
    requestPark<ParkAccessApplication>(
      `/bff/operator/access-applications/${uuid}/complete`,
      { method: 'POST' },
    ),
  cancelAccessApplication: (uuid: string, remark?: string) =>
    requestPark<ParkAccessApplication>(
      `/bff/operator/access-applications/${uuid}/cancel`,
      { method: 'POST', data: { remark } },
    ),
  listVehicles: (params?: EnterpriseVehicleListParams) =>
    requestPark<EnterpriseVehiclePageResult>('/bff/operator/vehicles', {
      method: 'GET',
      params,
    }),
  getVehicle: (uuid: string) =>
    requestPark<EnterpriseVehicle>(`/bff/operator/vehicles/${uuid}`, {
      method: 'GET',
    }),
  createOperatorVehicle: (
    data: CreateEnterpriseVehicleRequest & { enterpriseTenantUuid: string },
  ) =>
    requestPark<EnterpriseVehicle>('/bff/operator/vehicles', {
      method: 'POST',
      data,
    }),
  updateOperatorVehicle: (uuid: string, data: CreateEnterpriseVehicleRequest) =>
    requestPark<EnterpriseVehicle>(`/bff/operator/vehicles/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  approveVehicle: (uuid: string) =>
    requestPark<EnterpriseVehicle>(`/bff/operator/vehicles/${uuid}/approve`, {
      method: 'POST',
    }),
  rejectVehicle: (uuid: string, remark?: string) =>
    requestPark<EnterpriseVehicle>(`/bff/operator/vehicles/${uuid}/reject`, {
      method: 'POST',
      data: { remark },
    }),
  enableVehicle: (uuid: string) =>
    requestPark<EnterpriseVehicle>(`/bff/operator/vehicles/${uuid}/enable`, {
      method: 'POST',
    }),
  disableVehicle: (uuid: string) =>
    requestPark<EnterpriseVehicle>(`/bff/operator/vehicles/${uuid}/disable`, {
      method: 'POST',
    }),
  listInvestmentProjects: (params?: Batch3AListParams) =>
    requestPark<{
      items: InvestmentProject[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/investment-projects', { method: 'GET', params }),
  getInvestmentProject: (uuid: string) =>
    requestPark<InvestmentProject>(
      `/bff/operator/investment-projects/${uuid}`,
      { method: 'GET' },
    ),
  createInvestmentProject: (data: InvestmentProjectSaveRequest) =>
    requestPark<InvestmentProject>('/bff/operator/investment-projects', {
      method: 'POST',
      data,
    }),
  listInvestmentOrders: (
    params?: import('./types/investment-order').InvestmentOrderQuery,
  ) =>
    requestPark<{
      items: import('./types/investment-order').InvestmentOrder[];
      total: number;
    }>('/bff/operator/investment-orders', { method: 'GET', params }),
  getInvestmentContractPreparationOptions: () =>
    requestPark<
      import('./types/investment-order').InvestmentContractPreparationOptions
    >('/bff/operator/investment-orders/contract-preparation/options', {
      method: 'GET',
    }),
  getInvestmentOrder: (uuid: string, taskUuid?: string) =>
    requestPark<import('./types/investment-order').InvestmentOrder>(
      `/bff/operator/investment-orders/${uuid}${taskUuid ? '/review' : ''}`,
      { method: 'GET', params: taskUuid ? { taskUuid } : undefined },
    ),
  // 订单开票申请及财务返回的发票明细。
  getInvestmentOrderInvoices: (uuid: string) =>
    requestPark<import('./types/investment-order').InvestmentOrderInvoices>(
      `/bff/operator/investment-orders/${uuid}/invoices`,
      { method: 'GET' },
    ),
  createInvestmentOrderInvoiceApplication: (
    uuid: string,
    data: import('./types/investment-order').InvestmentInvoiceApplicationRequest,
    idempotencyKey: string,
  ) =>
    requestPark<import('./types/investment-order').InvestmentInvoiceApplication>(
      `/bff/operator/investment-orders/${uuid}/invoice-applications`,
      { method: 'POST', data, mutation: { idempotencyKey } },
    ),
  // 合同审批通过后生成订单（同一合同幂等）。
  createInvestmentOrderFromContract: (contractUuid: string) =>
    requestPark<import('./types/investment-order').InvestmentOrder>(
      `/bff/operator/investment-orders/from-contract/${contractUuid}`,
      { method: 'POST' },
    ),
  createInvestmentOrder: (data: { contractUuid: string }) =>
    requestPark<import('./types/investment-order').InvestmentOrder>(
      `/bff/operator/investment-orders/from-contract/${data.contractUuid}`,
      { method: 'POST' },
    ),
  updateInvestmentOrder: (
    uuid: string,
    data: import('./types/investment-order').InvestmentOrderSave,
  ) =>
    requestPark<import('./types/investment-order').InvestmentOrder>(
      `/bff/operator/investment-orders/${uuid}`,
      { method: 'PATCH', data },
    ),
  // 保存手写签署件与首期款付款截图。
  saveInvestmentOrderMaterials: (
    uuid: string,
    data: {
      signedFileUuid?: string | null;
      paymentProofFileUuid?: string | null;
    },
  ) =>
    requestPark<import('./types/investment-order').InvestmentOrder>(
      `/bff/operator/investment-orders/${uuid}/returned-files`,
      { method: 'PATCH', data },
    ),
  createInvestmentReceipt: (uuid: string) =>
    requestPark<import('./types/investment-order').InvestmentOrder>(
      `/bff/operator/investment-orders/${uuid}/receipt`,
      { method: 'POST' },
    ),
  verifyInvestmentReceipt: (
    uuid: string,
    data: {
      reference: string;
      amount: number;
      receivedAt: string;
      remark?: string;
    },
  ) =>
    requestPark<import('./types/investment-order').InvestmentOrder>(
      `/bff/operator/investment-orders/${uuid}/receipt/verify`,
      { method: 'POST', data },
    ),
  // 提交财务审核与人工登记审核结果（暂不接审批流）。
  submitInvestmentOrderFinance: (uuid: string) =>
    requestPark(`/bff/operator/investment-orders/${uuid}/submit-finance`, {
      method: 'POST',
    }),
  reviewInvestmentOrderFinance: (
    uuid: string,
    data: import('./types/investment-contract').ManualOrderFinanceReviewRequest,
  ) =>
    requestPark(`/bff/operator/investment-orders/${uuid}/finance-review`, {
      method: 'POST',
      data,
    }),
  // 下载合同审批通过时冻结的模板原件。
  getInvestmentOrderTemplate: (uuid: string) =>
    requestPark<import('./types/park').ServiceFileDownload>(
      `/bff/operator/investment-orders/${uuid}/template`,
      { method: 'GET' },
    ),
  // 订单侧三类材料：template=冻结的审批稿、signed=手写签署件、payment-proof=首期款截图。
  getInvestmentOrderFile: (
    uuid: string,
    kind: 'template' | 'signed' | 'payment-proof',
  ) =>
    requestPark<import('./types/park').ServiceFileDownload>(
      `/bff/operator/investment-orders/${uuid}/files/${kind}`,
      { method: 'GET' },
    ),
  syncInvestmentOrderWorkflow: (uuid: string) =>
    requestPark('/bff/workflow/instances/sync', {
      method: 'POST',
      data: { businessType: 'INVESTMENT_ORDER', businessUuid: uuid },
    }),
  withdrawInvestmentOrderWorkflow: (uuid: string) =>
    requestPark('/bff/workflow/instances/withdraw', {
      method: 'POST',
      data: { businessType: 'INVESTMENT_ORDER', businessUuid: uuid },
    }),
  // ============ 合同（/contract）============
  listInvestmentContracts: (
    params?: Omit<
      import('./types/investment-order').InvestmentOrderQuery,
      'status'
    > & {
      status?: import('./types/investment-contract').InvestmentContractStatus;
      contractType?: string;
      mine?: boolean;
      hasSupplement?: boolean;
    },
  ) =>
    requestPark<{
      items: import('./types/investment-contract').InvestmentContract[];
      total: number;
    }>('/bff/operator/investment-contracts', { method: 'GET', params }),
  getInvestmentContract: (uuid: string) =>
    requestPark<import('./types/investment-contract').InvestmentContract>(
      `/bff/operator/investment-contracts/${uuid}`,
      { method: 'GET' },
    ),
  listContractSupplements: (contractUuid: string) =>
    requestPark<{ items: import('./types/investment-contract').ContractSupplement[]; total: number }>(
      `/bff/operator/investment-contracts/${contractUuid}/supplements`,
      { method: 'GET' },
    ),
  listEnterpriseLibraryInvestmentOrders: (parkEnterpriseUuid: string, params?: { pageNumber?: number; pageSize?: number }) =>
    requestPark<{ items: Array<{ uuid: string; orderNo: string; title: string; sourceContractUuid: string; businessType: string; contractAmount: number; firstPaymentAmount: number; settledAmount: number; hasReceipt: boolean; createdAt: string }>; total: number }>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/investment-orders`, { method: 'GET', params },
    ),
  createContractSupplement: (
    contractUuid: string,
    data: import('./types/investment-contract').ContractSupplementSaveRequest,
  ) => requestPark<import('./types/investment-contract').ContractSupplement>(
    `/bff/operator/investment-contracts/${contractUuid}/supplements`,
    { method: 'POST', data, mutation: { actionId: 'contract-supplement.create' } },
  ),
  getContractSupplement: (uuid: string) =>
    requestPark<import('./types/investment-contract').ContractSupplement>(
      `/bff/operator/contract-supplements/${uuid}`, { method: 'GET' },
    ),
  updateContractSupplement: (
    uuid: string,
    data: import('./types/investment-contract').ContractSupplementSaveRequest,
  ) => requestPark<import('./types/investment-contract').ContractSupplement>(
    `/bff/operator/contract-supplements/${uuid}`,
    { method: 'PATCH', data, mutation: { actionId: 'contract-supplement.update' } },
  ),
  submitContractSupplement: (uuid: string) =>
    requestPark<import('./types/investment-contract').ContractSupplement>(
      `/bff/operator/contract-supplements/${uuid}/submit`,
      { method: 'POST', mutation: { actionId: 'contract-supplement.submit' } },
    ),
  reviewContractSupplement: (
    uuid: string,
    data: import('./types/investment-contract').ManualInvestmentReviewRequest,
  ) => requestPark<import('./types/investment-contract').ContractSupplement>(
    `/bff/operator/contract-supplements/${uuid}/manual-review`,
    { method: 'POST', data, mutation: { actionId: 'contract-supplement.review' } },
  ),
  generateContractSupplementWord: (uuid: string) =>
    requestPark<import('./types/investment-contract').ContractSupplement>(
      `/bff/operator/contract-supplements/${uuid}/generate-word`,
      { method: 'POST', mutation: { actionId: 'contract-supplement.generate' } },
    ),
  getContractSupplementFile: (uuid: string) =>
    requestPark<{ url: string; fileName: string }>(
      `/bff/operator/contract-supplements/${uuid}/files/draft`, { method: 'GET' },
    ),
  renewInvestmentContract: (uuid: string, data: import('./types/investment-contract').ContractRenewalRequest) =>
    requestPark<import('./types/investment-contract').InvestmentContract>(
      `/bff/operator/investment-contracts/${uuid}/renewals`, { method: 'POST', data },
    ),
  createInvestmentContract: (
    data: import('./types/investment-order').InvestmentOrderSave & {
      leadUuid?: string | null;
      contractType?: string;
    },
  ) =>
    requestPark<import('./types/investment-contract').InvestmentContract>(
      '/bff/operator/investment-contracts',
      { method: 'POST', data },
    ),
  updateInvestmentContract: (
    uuid: string,
    data: import('./types/investment-order').InvestmentOrderSave & { contractType?: string },
  ) =>
    requestPark<import('./types/investment-contract').InvestmentContract>(
      `/bff/operator/investment-contracts/${uuid}`,
      { method: 'PATCH', data },
    ),
  listContractReviewRoles: (params: { keyword?: string; pageNumber?: number; pageSize?: number }) =>
    requestPark<{ items: { code: string; name: string; namespace: string }[]; total: number; pageNumber: number; pageSize: number }>(
      '/bff/operator/contract-billing-config/review-roles', { method: 'GET', params },
    ),
  getContractCatalog: () => requestPark<import('./types/investment-contract').ContractCatalog>(
    '/bff/operator/contract-billing-config/catalog', { method: 'GET' },
  ),
  saveContractCatalog: (data: Pick<import('./types/investment-contract').ContractCatalog, 'revision' | 'types' | 'templates'>) =>
    requestPark<import('./types/investment-contract').ContractCatalog>(
      '/bff/operator/contract-billing-config/catalog', { method: 'PUT', data },
    ),
  // 提交合同审核与人工登记审核结果（暂不接审批流）。
  getInvestmentContractWordOptions: (uuid: string) =>
    requestPark<import('./types/investment-contract').ContractWordOptions>(
      `/bff/operator/investment-contracts/${uuid}/word-options`, { method: 'GET' },
    ),
  saveInvestmentContractWordTemplate: (
    uuid: string,
    data: { templateCode: string; businessType: import('./types/investment-order').InvestmentBusinessType },
  ) => requestPark<import('./types/investment-contract').InvestmentContract>(
    `/bff/operator/investment-contracts/${uuid}/word-template`, { method: 'PATCH', data },
  ),
  generateInvestmentContractWord: (uuid: string, data: { fingerprint: string; fields: Record<string, string> }) =>
    requestPark<import('./types/investment-contract').InvestmentContract>(
      `/bff/operator/investment-contracts/${uuid}/generate-word`, { method: 'POST', data },
    ),
  saveInvestmentContractWordFields: (uuid: string, data: { fingerprint: string; fields: Record<string, string> }) =>
    requestPark<import('./types/investment-contract').InvestmentContract>(
      `/bff/operator/investment-contracts/${uuid}/word-fields`, { method: 'PATCH', data },
    ),
  confirmInvestmentContractApproval: (uuid: string, data: { fingerprint: string }) =>
    requestPark<import('./types/investment-contract').InvestmentContract>(
      `/bff/operator/investment-contracts/${uuid}/confirm-approval`, { method: 'POST', data },
    ),
  submitInvestmentContract: (uuid: string) =>
    requestPark<import('./types/investment-contract').InvestmentContract>(`/bff/operator/investment-contracts/${uuid}/submit`, {
      method: 'POST',
    }),
  reviewInvestmentContract: (
    uuid: string,
    data: import('./types/investment-contract').ManualInvestmentReviewRequest,
  ) =>
    requestPark(`/bff/operator/investment-contracts/${uuid}/approval`, {
      method: 'POST',
      data,
    }),
  getInvestmentContractFile: (
    uuid: string,
    _kind: 'draft' | 'returned' = 'draft',
  ) =>
    requestPark<{ url: string; fileName: string }>(
      `/bff/operator/investment-contracts/${uuid}/files/${_kind}`,
      { method: 'GET' },
    ),
  // 签署件与首期款截图已移至订单侧。
  returnInvestmentContract: (_uuid: string, _fileUuid: string) =>
    Promise.reject(new Error('合同签署件回传已移至订单，请使用订单材料上传')),
  archiveInvestmentContract: (_uuid: string) =>
    Promise.reject(new Error('合同归档已移至订单，请使用订单签署件上传')),
  // ============ 计费规则配置 ============
  getContractBillingConfig: () =>
    requestPark<import('./types/investment-contract').ContractBillingConfig>(
      '/bff/operator/contract-billing-config',
      { method: 'GET' },
    ),
  saveContractBillingConfig: (
    rules: import('./types/investment-contract').ContractBillingRules,
  ) =>
    requestPark<import('./types/investment-contract').ContractBillingConfig>(
      '/bff/operator/contract-billing-config',
      { method: 'PUT', data: rules },
    ),
  listInvestmentLeads: (
    params?: import('./types/park').ParkInvestmentLeadQuery,
  ) =>
    requestPark<{
      items: import('./types/park').ParkInvestmentLead[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/investment-leads', { method: 'GET', params }),
  getInvestmentLeadSummary: () =>
    requestPark<import('./types/park').ParkInvestmentLeadSummary>(
      '/bff/operator/investment-leads/summary',
      { method: 'GET' },
    ),
  listInvestmentLeadOwners: () =>
    requestPark<import('./types/park').ParkInvestmentLeadOwner[]>(
      '/bff/operator/investment-leads/owners',
      { method: 'GET' },
    ),
  getInvestmentLead: (uuid: string) =>
    requestPark<import('./types/park').ParkInvestmentLead>(
      `/bff/operator/investment-leads/${uuid}`,
      { method: 'GET' },
    ),
  createInvestmentLead: (
    data: import('./types/park').ParkInvestmentLeadSaveRequest,
  ) =>
    requestPark<import('./types/park').ParkInvestmentLead>(
      '/bff/operator/investment-leads',
      { method: 'POST', data },
    ),
  updateInvestmentLead: (
    uuid: string,
    data: import('./types/park').ParkInvestmentLeadSaveRequest,
  ) =>
    requestPark<import('./types/park').ParkInvestmentLead>(
      `/bff/operator/investment-leads/${uuid}`,
      { method: 'PATCH', data },
    ),
  addInvestmentLeadFollowUp: (
    uuid: string,
    data: { action: string; content: string; nextFollowUpAt?: string },
  ) =>
    requestPark(`/bff/operator/investment-leads/${uuid}/follow-ups`, {
      method: 'POST',
      data,
    }),
  changeInvestmentLeadStage: (
    uuid: string,
    data: {
      stage: import('./types/park').ParkInvestmentLeadStage;
      transitionMode?: import('./types/park').ParkInvestmentLeadStageTransitionMode;
      coveredStages?: import('./types/park').ParkInvestmentLeadStage[] | null;
      remark?: string;
    },
  ) =>
    requestPark<import('./types/park').ParkInvestmentLead>(
      `/bff/operator/investment-leads/${uuid}/stage`,
      { method: 'POST', data },
    ),
  createInvestmentLeadLeasePlan: (
    uuid: string,
    data: import('./types/park').CreateParkLeasePlanRequest,
  ) =>
    requestPark<{
      leadUuid: string;
      leasePlan: import('./types/park').ParkLeasePlan;
    }>(`/bff/operator/investment-leads/${uuid}/lease-plan`, {
      method: 'POST',
      data,
    }),
  handoverInvestmentLead: (
    uuid: string,
    data: import('./types/park').ParkInvestmentLeadHandoverRequest,
  ) =>
    requestPark<import('./types/park').ParkInvestmentLeadHandoverResult>(
      `/bff/operator/investment-leads/${uuid}/handover`,
      { method: 'POST', data },
    ),
  listInvestmentLeadHandovers: (uuid: string) =>
    requestPark<import('./types/park').ParkInvestmentLeadHandoverRecord[]>(
      `/bff/operator/investment-leads/${uuid}/handovers`,
      { method: 'GET' },
    ),
  // ---- 招商 CRM 四对象：线索 / 客户 / 联系人 / 公海私池 ----
  listCrmLeads: (params?: import('./types/park').ParkCrmLeadQuery) =>
    requestPark<{
      items: import('./types/park').ParkCrmLead[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/crm-leads', { method: 'GET', params }),
  getCrmLeadSummary: () =>
    requestPark<import('./types/park').ParkCrmLeadSummary>(
      '/bff/operator/crm-leads/summary',
      { method: 'GET' },
    ),
  listCrmLeadOwners: () =>
    requestPark<import('./types/park').ParkCrmOwner[]>(
      '/bff/operator/crm-leads/owners',
      { method: 'GET' },
    ),
  getCrmLead: (uuid: string) =>
    requestPark<import('./types/park').ParkCrmLead>(
      `/bff/operator/crm-leads/${uuid}`,
      { method: 'GET' },
    ),
  createCrmLead: (data: import('./types/park').ParkCrmLeadSaveRequest) =>
    requestPark<import('./types/park').ParkCrmLead>('/bff/operator/crm-leads', {
      method: 'POST',
      data,
      mutation: { actionId: 'crm-lead.create' },
    }),
  updateCrmLead: (
    uuid: string,
    data: import('./types/park').ParkCrmLeadSaveRequest,
  ) =>
    requestPark<import('./types/park').ParkCrmLead>(
      `/bff/operator/crm-leads/${uuid}`,
      { method: 'PATCH', data, mutation: { actionId: 'crm-lead.update' } },
    ),
  claimCrmLead: (uuid: string) =>
    requestPark<import('./types/park').ParkCrmLead>(
      `/bff/operator/crm-leads/${uuid}/claim`,
      { method: 'POST' },
    ),
  poolCrmLead: (uuid: string, data: { reason?: string }) =>
    requestPark<import('./types/park').ParkCrmLead>(
      `/bff/operator/crm-leads/${uuid}/pool`,
      { method: 'POST', data },
    ),
  assignCrmLead: (
    uuid: string,
    data: { memberUuid: string; reason?: string },
  ) =>
    requestPark<import('./types/park').ParkCrmLead>(
      `/bff/operator/crm-leads/${uuid}/assign`,
      { method: 'POST', data },
    ),
  addCrmLeadFollowUp: (
    uuid: string,
    data: { action: string; content: string; nextFollowUpAt?: string | null },
  ) =>
    requestPark(`/bff/operator/crm-leads/${uuid}/follow-ups`, {
      method: 'POST',
      data,
    }),
  convertCrmLead: (
    uuid: string,
    data: import('./types/park').ConvertParkCrmLeadRequest,
  ) =>
    requestPark<import('./types/park').ConvertParkCrmLeadResult>(
      `/bff/operator/crm-leads/${uuid}/convert`,
      { method: 'POST', data },
    ),
  listCrmCustomers: (params?: import('./types/park').ParkCrmCustomerQuery) =>
    requestPark<{
      items: import('./types/park').ParkCrmCustomer[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/crm-customers', { method: 'GET', params }),
  getCrmCustomerSummary: () =>
    requestPark<import('./types/park').ParkCrmCustomerSummary>(
      '/bff/operator/crm-customers/summary',
      { method: 'GET' },
    ),
  listCrmCustomerOwners: () =>
    requestPark<import('./types/park').ParkCrmOwner[]>(
      '/bff/operator/crm-customers/owners',
      { method: 'GET' },
    ),
  getCrmCustomer: (uuid: string) =>
    requestPark<import('./types/park').ParkCrmCustomer>(
      `/bff/operator/crm-customers/${uuid}`,
      { method: 'GET' },
    ),
  createCrmCustomer: (
    data: import('./types/park').ParkCrmCustomerSaveRequest,
  ) =>
    requestPark<import('./types/park').ParkCrmCustomer>(
      '/bff/operator/crm-customers',
      { method: 'POST', data },
    ),
  updateCrmCustomer: (
    uuid: string,
    data: import('./types/park').ParkCrmCustomerSaveRequest,
  ) =>
    requestPark<import('./types/park').ParkCrmCustomer>(
      `/bff/operator/crm-customers/${uuid}`,
      { method: 'PATCH', data },
    ),
  claimCrmCustomer: (uuid: string) =>
    requestPark<import('./types/park').ParkCrmCustomer>(
      `/bff/operator/crm-customers/${uuid}/claim`,
      { method: 'POST' },
    ),
  poolCrmCustomer: (uuid: string, data: { reason?: string }) =>
    requestPark<import('./types/park').ParkCrmCustomer>(
      `/bff/operator/crm-customers/${uuid}/pool`,
      { method: 'POST', data },
    ),
  assignCrmCustomer: (
    uuid: string,
    data: { memberUuid: string; reason?: string },
  ) =>
    requestPark<import('./types/park').ParkCrmCustomer>(
      `/bff/operator/crm-customers/${uuid}/assign`,
      { method: 'POST', data },
    ),
  terminateCrmCustomer: (
    uuid: string,
    data: import('./types/park').TerminateParkCrmCustomerRequest,
  ) =>
    requestPark<import('./types/park').TerminateParkCrmCustomerResult>(
      `/bff/operator/crm-customers/${uuid}/terminate`,
      { method: 'POST', data },
    ),
  listCrmContacts: (params?: import('./types/park').ParkCrmContactQuery) =>
    requestPark<{
      items: import('./types/park').ParkCrmContact[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/crm-contacts', { method: 'GET', params }),
  getCrmContact: (uuid: string) =>
    requestPark<import('./types/park').ParkCrmContact>(
      `/bff/operator/crm-contacts/${uuid}`,
      { method: 'GET' },
    ),
  createCrmContact: (data: import('./types/park').ParkCrmContactSaveRequest) =>
    requestPark<import('./types/park').ParkCrmContact>(
      '/bff/operator/crm-contacts',
      { method: 'POST', data },
    ),
  updateCrmContact: (
    uuid: string,
    data: import('./types/park').ParkCrmContactSaveRequest,
  ) =>
    requestPark<import('./types/park').ParkCrmContact>(
      `/bff/operator/crm-contacts/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteCrmContact: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/crm-contacts/${uuid}`, {
      method: 'DELETE',
    }),
  getCrmPoolSetting: () =>
    requestPark<import('./types/park').ParkCrmPoolSetting>(
      '/bff/operator/crm-pool/settings',
      { method: 'GET' },
    ),
  updateCrmPoolSetting: (
    data: import('./types/park').ParkCrmPoolSettingSaveRequest,
  ) =>
    requestPark<import('./types/park').ParkCrmPoolSetting>(
      '/bff/operator/crm-pool/settings',
      { method: 'PATCH', data },
    ),
  listCrmPoolLogs: (params?: {
    objectType?: import('./types/park').ParkCrmPoolObjectType;
    objectUuid?: string;
  }) =>
    requestPark<import('./types/park').ParkCrmPoolLog[]>(
      '/bff/operator/crm-pool/logs',
      { method: 'GET', params },
    ),
  sweepCrmPool: () =>
    requestPark<import('./types/park').ParkCrmSweepResult>(
      '/bff/operator/crm-pool/sweep',
      { method: 'POST' },
    ),
  updateInvestmentProject: (
    uuid: string,
    data: Partial<InvestmentProjectSaveRequest>,
  ) =>
    requestPark<InvestmentProject>(
      `/bff/operator/investment-projects/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteInvestmentProject: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/investment-projects/${uuid}`, {
      method: 'DELETE',
    }),
  updateInvestmentProjectStatus: (
    uuid: string,
    status: InvestmentProject['status'],
  ) =>
    requestPark<InvestmentProject>(
      `/bff/operator/investment-projects/${uuid}/status`,
      { method: 'POST', data: { status } },
    ),
  getInvestmentIntentStatusSummary: () =>
    requestPark<InvestmentIntentStatusSummary>(
      '/bff/operator/investment-intents/summary',
      { method: 'GET' },
    ),
  listInvestmentIntents: (projectUuid: string, params?: Batch3AListParams) =>
    requestPark<{
      items: InvestmentIntent[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>(`/bff/operator/investment-projects/${projectUuid}/intents`, {
      method: 'GET',
      params,
    }),
  getInvestmentIntent: (uuid: string) =>
    requestPark<InvestmentIntent>(`/bff/operator/investment-intents/${uuid}`, {
      method: 'GET',
    }),
  updateInvestmentIntentStatus: (
    uuid: string,
    status: InvestmentIntent['status'],
    remark?: string,
  ) =>
    requestPark<InvestmentIntent>(
      `/bff/operator/investment-intents/${uuid}/status`,
      { method: 'POST', data: { status, remark } },
    ),
  addInvestmentIntentFollowUp: (
    uuid: string,
    data: { action: string; content?: string },
  ) =>
    requestPark<InvestmentIntent>(
      `/bff/operator/investment-intents/${uuid}/follow-ups`,
      { method: 'POST', data },
    ),
  convertInvestmentIntentToLead: (uuid: string) =>
    requestPark<{
      intent: InvestmentIntent;
      lead: import('./types/park').ParkCrmLead;
      converted: boolean;
    }>(`/bff/operator/investment-intents/${uuid}/convert-to-lead`, {
      method: 'POST',
    }),
  listIncubationPrograms: (params?: Batch3AListParams) =>
    requestPark<{
      items: IncubationProgram[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>('/bff/operator/incubation-programs', { method: 'GET', params }),
  getIncubationProgram: (uuid: string) =>
    requestPark<IncubationProgram>(
      `/bff/operator/incubation-programs/${uuid}`,
      { method: 'GET' },
    ),
  createIncubationProgram: (data: IncubationProgramSaveRequest) =>
    requestPark<IncubationProgram>('/bff/operator/incubation-programs', {
      method: 'POST',
      data,
    }),
  updateIncubationProgram: (
    uuid: string,
    data: Partial<IncubationProgramSaveRequest>,
  ) =>
    requestPark<IncubationProgram>(
      `/bff/operator/incubation-programs/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteIncubationProgram: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/incubation-programs/${uuid}`, {
      method: 'DELETE',
    }),
  updateIncubationProgramStatus: (
    uuid: string,
    status: IncubationProgram['status'],
  ) =>
    requestPark<IncubationProgram>(
      `/bff/operator/incubation-programs/${uuid}/status`,
      { method: 'POST', data: { status } },
    ),
  listIncubationApplications: (
    programUuid: string,
    params?: Batch3AListParams,
  ) =>
    requestPark<{
      items: IncubationApplication[];
      pageNumber: number;
      pageSize: number;
      total: number;
    }>(`/bff/operator/incubation-programs/${programUuid}/applications`, {
      method: 'GET',
      params,
    }),
  getIncubationApplication: (uuid: string) =>
    requestPark<IncubationApplication>(
      `/bff/operator/incubation-applications/${uuid}`,
      { method: 'GET' },
    ),
  updateIncubationApplicationStatus: (
    uuid: string,
    status: IncubationApplication['status'],
    remark?: string,
  ) =>
    requestPark<IncubationApplication>(
      `/bff/operator/incubation-applications/${uuid}/status`,
      { method: 'POST', data: { status, remark } },
    ),
  listPortalPages: (params?: PortalPageListParams) =>
    requestPark<PortalPagePageResult>('/bff/operator/portal-pages', {
      method: 'GET',
      params,
    }),
  getPortalPage: (uuid: string) =>
    requestPark<PortalPage>(`/bff/operator/portal-pages/${uuid}`, {
      method: 'GET',
    }),
  createPortalPage: (data: PortalPageSaveRequest) =>
    requestPark<PortalPage>('/bff/operator/portal-pages', {
      method: 'POST',
      data,
    }),
  updatePortalPage: (uuid: string, data: Partial<PortalPageSaveRequest>) =>
    requestPark<PortalPage>(`/bff/operator/portal-pages/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  updatePortalPageStatus: (uuid: string, status: PortalRecordStatus) =>
    requestPark<PortalPage>(`/bff/operator/portal-pages/${uuid}/status`, {
      method: 'POST',
      data: { status },
    }),
  deletePortalPage: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/portal-pages/${uuid}`, {
      method: 'DELETE',
    }),
  listPortalColumns: (params?: PortalColumnListParams) =>
    requestPark<PortalColumnListResult>('/bff/operator/portal-columns', {
      method: 'GET',
      params,
    }),
  getPortalColumn: (uuid: string) =>
    requestPark<PortalColumn>(`/bff/operator/portal-columns/${uuid}`, {
      method: 'GET',
    }),
  createPortalColumn: (data: PortalColumnSaveRequest) =>
    requestPark<PortalColumn>('/bff/operator/portal-columns', {
      method: 'POST',
      data,
    }),
  updatePortalColumn: (uuid: string, data: PortalColumnPatchRequest) =>
    requestPark<PortalColumn>(`/bff/operator/portal-columns/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  updatePortalColumnStatus: (uuid: string, status: PortalRecordStatus) =>
    requestPark<PortalColumn>(`/bff/operator/portal-columns/${uuid}/status`, {
      method: 'POST',
      data: { status },
    }),
  deletePortalColumn: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/portal-columns/${uuid}`, {
      method: 'DELETE',
    }),
  listPortalContents: (params?: PortalContentListParams) =>
    requestPark<PortalContentPageResult>('/bff/operator/portal-contents', {
      method: 'GET',
      params,
    }),
  getPortalContent: (uuid: string) =>
    requestPark<PortalContent>(`/bff/operator/portal-contents/${uuid}`, {
      method: 'GET',
    }),
  createPortalContent: (data: PortalContentSaveRequest) =>
    requestPark<PortalContent>('/bff/operator/portal-contents', {
      method: 'POST',
      data,
    }),
  updatePortalContent: (
    uuid: string,
    data: Partial<PortalContentSaveRequest>,
  ) =>
    requestPark<PortalContent>(`/bff/operator/portal-contents/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  updatePortalContentStatus: (uuid: string, status: PortalContentStatus) =>
    requestPark<PortalContent>(`/bff/operator/portal-contents/${uuid}/status`, {
      method: 'POST',
      data: { status },
    }),
  deletePortalContent: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/portal-contents/${uuid}`, {
      method: 'DELETE',
    }),
  listPortalBanners: (params?: PortalBannerListParams) =>
    requestPark<PortalBannerPageResult>('/bff/operator/portal-banners', {
      method: 'GET',
      params,
    }),
  getPortalBanner: (uuid: string) =>
    requestPark<PortalBanner>(`/bff/operator/portal-banners/${uuid}`, {
      method: 'GET',
    }),
  createPortalBanner: (data: PortalBannerSaveRequest) =>
    requestPark<PortalBanner>('/bff/operator/portal-banners', {
      method: 'POST',
      data,
    }),
  updatePortalBanner: (uuid: string, data: Partial<PortalBannerSaveRequest>) =>
    requestPark<PortalBanner>(`/bff/operator/portal-banners/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  updatePortalBannerStatus: (uuid: string, status: PortalBannerStatus) =>
    requestPark<PortalBanner>(`/bff/operator/portal-banners/${uuid}/status`, {
      method: 'POST',
      data: { status },
    }),
  movePortalBanner: (uuid: string, direction: 'up' | 'down') =>
    requestPark<PortalBanner>(`/bff/operator/portal-banners/${uuid}/move`, {
      method: 'POST',
      data: { direction },
    }),
  deletePortalBanner: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/portal-banners/${uuid}`, {
      method: 'DELETE',
    }),
  getPortalSetting: () =>
    requestPark<PortalSetting | null>('/bff/operator/portal-settings/current', {
      method: 'GET',
    }),
  savePortalSetting: (data: SavePortalSettingRequest) =>
    requestPark<PortalSetting>('/bff/operator/portal-settings/current', {
      method: 'PUT',
      data,
    }),
  getSpaceConfig: () =>
    requestPark<SpaceConfigGroup[]>('/bff/operator/space-config', {
      method: 'GET',
    }),
  createSpaceConfigItem: (
    kind: SpaceConfigKind,
    data: SpaceConfigItemCreateRequest,
  ) =>
    requestPark<SpaceConfigItem>(`/bff/operator/space-config/${kind}/items`, {
      method: 'POST',
      data,
    }),
  updateSpaceConfigItem: (
    kind: SpaceConfigKind,
    itemUuid: string,
    data: SpaceConfigItemUpdateRequest,
  ) =>
    requestPark<SpaceConfigItem>(
      `/bff/operator/space-config/${kind}/items/${itemUuid}`,
      { method: 'PATCH', data },
    ),
  deleteSpaceConfigItem: (kind: SpaceConfigKind, itemUuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/space-config/${kind}/items/${itemUuid}`,
      { method: 'DELETE' },
    ),
  ensureDictionary: (data: EnsureDictionaryRequest) =>
    requestPark<Dictionary>('/bff/operator/dictionaries/ensure', {
      method: 'POST',
      data,
    }),
  listDictionaries: (params?: DictionaryListParams) =>
    requestPark<PageResult<Dictionary>>('/bff/operator/dictionaries', {
      method: 'GET',
      params,
    }),
  getDictionary: (uuid: string) =>
    requestPark<Dictionary>(`/bff/operator/dictionaries/${uuid}`, {
      method: 'GET',
    }),
  createDictionary: (data: DictionarySaveRequest) =>
    requestPark<Dictionary>('/bff/operator/dictionaries', {
      method: 'POST',
      data,
    }),
  updateDictionary: (uuid: string, data: Partial<DictionarySaveRequest>) =>
    requestPark<Dictionary>(`/bff/operator/dictionaries/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  deleteDictionary: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/dictionaries/${uuid}`, {
      method: 'DELETE',
    }),
  listDictionaryItems: (uuid: string) =>
    requestPark<DictionaryItem[]>(`/bff/operator/dictionaries/${uuid}/items`, {
      method: 'GET',
    }),
  createDictionaryItem: (uuid: string, data: DictionaryItemSaveRequest) =>
    requestPark<DictionaryItem>(`/bff/operator/dictionaries/${uuid}/items`, {
      method: 'POST',
      data,
    }),
  updateDictionaryItem: (
    uuid: string,
    itemUuid: string,
    data: Partial<DictionaryItemSaveRequest>,
  ) =>
    requestPark<DictionaryItem>(
      `/bff/operator/dictionaries/${uuid}/items/${itemUuid}`,
      { method: 'PATCH', data },
    ),
  deleteDictionaryItem: (uuid: string, itemUuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/dictionaries/${uuid}/items/${itemUuid}`,
      { method: 'DELETE' },
    ),
  sortDictionaryItems: (uuid: string, itemUuids: string[]) =>
    requestPark<DictionaryItem[]>(
      `/bff/operator/dictionaries/${uuid}/items/sort`,
      { method: 'POST', data: { itemUuids } },
    ),
  listNumberRules: (params?: NumberRuleListParams) =>
    requestPark<PageResult<NumberRule>>('/bff/operator/number-rules', {
      method: 'GET',
      params,
    }),
  getNumberRule: (uuid: string) =>
    requestPark<NumberRule>(`/bff/operator/number-rules/${uuid}`, {
      method: 'GET',
    }),
  createNumberRule: (data: NumberRuleSaveRequest) =>
    requestPark<NumberRule>('/bff/operator/number-rules', {
      method: 'POST',
      data,
    }),
  updateNumberRule: (uuid: string, data: Partial<NumberRuleSaveRequest>) =>
    requestPark<NumberRule>(`/bff/operator/number-rules/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  deleteNumberRule: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/number-rules/${uuid}`, {
      method: 'DELETE',
    }),
  generateNumber: (uuid: string) =>
    requestPark<GeneratedNumber>(
      `/bff/operator/number-rules/${uuid}/generate`,
      {
        method: 'POST',
      },
    ),
  listGrowthIndicators: () =>
    requestPark<GrowthIndicator[]>('/bff/operator/growth-indicators', {
      method: 'GET',
    }),
  listGrowthModels: (params?: GrowthModelListParams) =>
    requestPark<PageResult<GrowthModel>>('/bff/operator/growth-models', {
      method: 'GET',
      params,
    }),
  getGrowthModel: (uuid: string) =>
    requestPark<GrowthModel>(`/bff/operator/growth-models/${uuid}`, {
      method: 'GET',
    }),
  createGrowthModel: (data: GrowthModelSaveRequest) =>
    requestPark<GrowthModel>('/bff/operator/growth-models', {
      method: 'POST',
      data,
    }),
  updateGrowthModel: (uuid: string, data: Partial<GrowthModelSaveRequest>) =>
    requestPark<GrowthModel>(`/bff/operator/growth-models/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  enableGrowthModel: (uuid: string) =>
    requestPark<GrowthModel>(`/bff/operator/growth-models/${uuid}/enable`, {
      method: 'POST',
    }),
  disableGrowthModel: (uuid: string) =>
    requestPark<GrowthModel>(`/bff/operator/growth-models/${uuid}/disable`, {
      method: 'POST',
    }),
  deleteGrowthModel: (uuid: string) =>
    requestPark<{ uuid: string }>(`/bff/operator/growth-models/${uuid}`, {
      method: 'DELETE',
    }),
  listGrowthStages: (uuid: string) =>
    requestPark<GrowthStage[]>(`/bff/operator/growth-models/${uuid}/stages`, {
      method: 'GET',
    }),
  createGrowthStage: (uuid: string, data: GrowthStageSaveRequest) =>
    requestPark<GrowthStage>(`/bff/operator/growth-models/${uuid}/stages`, {
      method: 'POST',
      data,
    }),
  updateGrowthStage: (
    uuid: string,
    stageUuid: string,
    data: Partial<GrowthStageSaveRequest>,
  ) =>
    requestPark<GrowthStage>(
      `/bff/operator/growth-models/${uuid}/stages/${stageUuid}`,
      { method: 'PATCH', data },
    ),
  deleteGrowthStage: (uuid: string, stageUuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/growth-models/${uuid}/stages/${stageUuid}`,
      { method: 'DELETE' },
    ),
  reorderGrowthStages: (uuid: string, stageUuids: string[]) =>
    requestPark<GrowthStage[]>(
      `/bff/operator/growth-models/${uuid}/stages/reorder`,
      { method: 'POST', data: { stageUuids } },
    ),
  createGrowthStageRule: (
    uuid: string,
    stageUuid: string,
    data: GrowthStageRuleSaveRequest,
  ) =>
    requestPark<GrowthStageRule>(
      `/bff/operator/growth-models/${uuid}/stages/${stageUuid}/rules`,
      { method: 'POST', data },
    ),
  updateGrowthStageRule: (
    uuid: string,
    stageUuid: string,
    ruleUuid: string,
    data: Partial<GrowthStageRuleSaveRequest>,
  ) =>
    requestPark<GrowthStageRule>(
      `/bff/operator/growth-models/${uuid}/stages/${stageUuid}/rules/${ruleUuid}`,
      { method: 'PATCH', data },
    ),
  deleteGrowthStageRule: (uuid: string, stageUuid: string, ruleUuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/growth-models/${uuid}/stages/${stageUuid}/rules/${ruleUuid}`,
      { method: 'DELETE' },
    ),
  listGrowthEnterpriseSummaries: (params?: GrowthEnterpriseSummaryQuery) =>
    requestPark<PageResult<GrowthEnterpriseSummary>>(
      '/bff/operator/growth-enterprise-summaries',
      { method: 'GET', params },
    ),
  evaluateEnterpriseGrowth: (
    enterpriseTenantUuid: string,
    data?: { evaluationDate?: string },
  ) =>
    requestPark<GrowthEvaluationResult>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/growth-evaluations`,
      { method: 'POST', data: data || {} },
    ),
  getEnterpriseGrowthProfile: (enterpriseTenantUuid: string) =>
    requestPark<{
      profile: GrowthEnterpriseSummary | null;
      latestEvaluation: GrowthEvaluation | null;
    }>(`/bff/operator/enterprises/${enterpriseTenantUuid}/growth-profile`, {
      method: 'GET',
    }),
  listEnterpriseGrowthArchives: (params?: GrowthEnterpriseSummaryQuery) =>
    requestPark<PageResult<GrowthEnterpriseSummary>>(
      '/bff/operator/enterprise-growth',
      { method: 'GET', params },
    ),
  getEnterpriseGrowthArchive: (enterpriseTenantUuid: string) =>
    requestPark<GrowthArchive>(
      `/bff/operator/enterprise-growth/${enterpriseTenantUuid}`,
      { method: 'GET' },
    ),
  evaluateEnterpriseGrowthArchive: (
    enterpriseTenantUuid: string,
    data?: { evaluationDate?: string },
  ) =>
    requestPark<GrowthEvaluationResult>(
      `/bff/operator/enterprise-growth/${enterpriseTenantUuid}/evaluate`,
      { method: 'POST', data: data || {} },
    ),
  updateEnterpriseGrowthCurrentStage: (
    enterpriseTenantUuid: string,
    data: { currentStageUuid: string; reason: string },
  ) =>
    requestPark<GrowthArchive>(
      `/bff/operator/enterprise-growth/${enterpriseTenantUuid}/current-stage`,
      { method: 'PUT', data },
    ),
  restoreEnterpriseGrowthEvaluatedStage: (enterpriseTenantUuid: string) =>
    requestPark<GrowthArchive>(
      `/bff/operator/enterprise-growth/${enterpriseTenantUuid}/restore-evaluated-stage`,
      { method: 'POST' },
    ),
  listEnterpriseGrowthHistories: (
    enterpriseTenantUuid: string,
    params?: PageQuery,
  ) =>
    requestPark<PageResult<GrowthStageHistory>>(
      `/bff/operator/enterprise-growth/${enterpriseTenantUuid}/history`,
      { method: 'GET', params },
    ),
  upsertGrowthManualIndicator: (
    enterpriseTenantUuid: string,
    indicatorCode: string,
    data: GrowthManualIndicatorSaveRequest,
  ) =>
    requestPark<unknown>(
      `/bff/operator/enterprises/${enterpriseTenantUuid}/growth-indicators/${indicatorCode}/manual`,
      { method: 'PUT', data },
    ),
  listGrowthRecommendationRules: (
    params?: GrowthRecommendationRuleListParams,
  ) =>
    requestPark<PageResult<GrowthRecommendationRule>>(
      '/bff/operator/growth-recommendation-rules',
      { method: 'GET', params },
    ),
  getGrowthRecommendationRule: (uuid: string) =>
    requestPark<GrowthRecommendationRule>(
      `/bff/operator/growth-recommendation-rules/${uuid}`,
      { method: 'GET' },
    ),
  createGrowthRecommendationRule: (data: GrowthRecommendationRuleSaveRequest) =>
    requestPark<GrowthRecommendationRule>(
      '/bff/operator/growth-recommendation-rules',
      { method: 'POST', data },
    ),
  updateGrowthRecommendationRule: (
    uuid: string,
    data: GrowthRecommendationRuleSaveRequest,
  ) =>
    requestPark<GrowthRecommendationRule>(
      `/bff/operator/growth-recommendation-rules/${uuid}`,
      { method: 'PUT', data },
    ),
  enableGrowthRecommendationRule: (uuid: string) =>
    requestPark<GrowthRecommendationRule>(
      `/bff/operator/growth-recommendation-rules/${uuid}/enable`,
      { method: 'POST' },
    ),
  disableGrowthRecommendationRule: (uuid: string) =>
    requestPark<GrowthRecommendationRule>(
      `/bff/operator/growth-recommendation-rules/${uuid}/disable`,
      { method: 'POST' },
    ),
  deleteGrowthRecommendationRule: (uuid: string) =>
    requestPark<{ uuid: string }>(
      `/bff/operator/growth-recommendation-rules/${uuid}`,
      { method: 'DELETE' },
    ),
  listGrowthRecommendationTargets: (params: {
    targetType: GrowthRecommendationTargetType;
    keyword?: string;
    pageNumber?: number;
    pageSize?: number;
  }) =>
    requestPark<PageResult<GrowthRecommendationTarget>>(
      '/bff/operator/growth-recommendation-targets',
      { method: 'GET', params },
    ),
  listEnterpriseGrowthRecommendations: (
    enterpriseTenantUuid: string,
    params?: GrowthRecommendationListParams,
  ) =>
    requestPark<PageResult<GrowthRecommendation>>(
      `/bff/operator/enterprise-growth/${enterpriseTenantUuid}/recommendations`,
      { method: 'GET', params },
    ),
  refreshEnterpriseGrowthRecommendations: (enterpriseTenantUuid: string) =>
    requestPark<PageResult<GrowthRecommendation>>(
      `/bff/operator/enterprise-growth/${enterpriseTenantUuid}/recommendations/refresh`,
      { method: 'POST' },
    ),
  updateEnterpriseGrowthRecommendationStatus: (
    enterpriseTenantUuid: string,
    uuid: string,
    status: GrowthRecommendationStatus,
  ) =>
    requestPark<GrowthRecommendation>(
      `/bff/operator/enterprise-growth/${enterpriseTenantUuid}/recommendations/${uuid}/status`,
      { method: 'PUT', data: { status } },
    ),
  getEnterpriseLibrarySummary: () =>
    requestPark<EnterpriseLibrarySummary>(
      '/bff/operator/enterprise-library/summary',
      { method: 'GET' },
    ),
  listEnterpriseLibrary: (params?: EnterpriseLibraryListQuery) =>
    requestPark<PageResult<EnterpriseLibraryListItem>>(
      '/bff/operator/enterprise-library',
      { method: 'GET', params },
    ),
  listEnterpriseLibrarySpaceOptions: (enterpriseType: ParkEnterpriseType) =>
    requestPark<EnterpriseLibrarySpaceOption[]>(
      '/bff/operator/enterprise-library/space-options',
      { method: 'GET', params: { enterpriseType } },
    ),
  getEnterpriseLibraryItem: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseLibraryDetail>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}`,
      { method: 'GET' },
    ),
  createEnterpriseLibraryItem: (data: CreateEnterpriseLibraryItemRequest) =>
    requestPark<CreateEnterpriseLibraryResult>(
      '/bff/operator/enterprise-library',
      { method: 'POST', data },
    ),
  updateEnterpriseLibraryItem: (
    parkEnterpriseUuid: string,
    data: PatchEnterpriseLibraryItemRequest,
  ) =>
    requestPark<EnterpriseLibraryDetail>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}`,
      { method: 'PATCH', data },
    ),
  downloadEnterpriseLibraryTemplate: () =>
    requestPark<EnterpriseLibraryImportTemplate>(
      '/bff/operator/enterprise-library/import/template',
      { method: 'GET' },
    ),
  exportEnterpriseLibrary: (params?: EnterpriseLibraryListQuery) =>
    requestPark<EnterpriseLibraryImportTemplate>(
      '/bff/operator/enterprise-library/export',
      { method: 'GET', params },
    ),
  importEnterpriseLibrary: (file: File) => {
    const data = new FormData();
    data.append('file', file);
    return requestPark<EnterpriseLibraryImportResult>(
      '/bff/operator/enterprise-library/import',
      { method: 'POST', data },
    );
  },
  getEnterpriseLibraryInvitation: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseLibraryInvitation>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/invitation`,
      { method: 'GET' },
    ),
  createEnterpriseLibraryInvitation: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseLibraryInvitation>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/invitation`,
      { method: 'POST' },
    ),
  cancelEnterpriseLibraryInvitation: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseLibraryInvitation>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/invitation/cancel`,
      { method: 'POST' },
    ),
  regenerateEnterpriseLibraryInvitation: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseLibraryInvitation>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/invitation/regenerate`,
      { method: 'POST' },
    ),
  batchCreateEnterpriseLibraryInvitations: (parkEnterpriseUuids: string[]) =>
    requestPark<EnterpriseLibraryBatchInvitationResult>(
      '/bff/operator/enterprise-library/invitations/batch',
      { method: 'POST', data: { parkEnterpriseUuids } },
    ),
  listEnterpriseLibraryClaims: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseLibraryClaimHistory>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/claims`,
      { method: 'GET' },
    ),
  listEnterpriseLibraryAdmissions: (parkEnterpriseUuid: string) =>
    requestPark<AdmissionApplication[]>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/admissions`,
      { method: 'GET' },
    ),
  listEnterpriseLibraryExits: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseLibraryExitRecord[]>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/exits`,
      { method: 'GET' },
    ),
  getEnterpriseLibraryClaimReview: (parkEnterpriseUuid: string) =>
    requestPark<EnterpriseLibraryClaimReview>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/claim`,
      { method: 'GET' },
    ),
  approveEnterpriseLibraryClaim: (parkEnterpriseUuid: string) =>
    requestPark<unknown>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/claim/approve`,
      { method: 'POST' },
    ),
  rejectEnterpriseLibraryClaim: (parkEnterpriseUuid: string, reason: string) =>
    requestPark<unknown>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/claim/reject`,
      { method: 'POST', data: { reason } },
    ),
  unclaimEnterpriseLibraryItem: (parkEnterpriseUuid: string, reason: string) =>
    requestPark<unknown>(
      `/bff/operator/enterprise-library/${parkEnterpriseUuid}/unclaim`,
      { method: 'POST', data: { reason } },
    ),
  listEnterpriseExits: (params?: EnterpriseExitListQuery) =>
    requestPark<EnterpriseExitListResult>('/bff/operator/enterprise-exits', {
      method: 'GET',
      params,
    }),
  getEnterpriseExit: (uuid: string) =>
    requestPark<EnterpriseExitDetail>(
      `/bff/operator/enterprise-exits/${uuid}`,
      {
        method: 'GET',
      },
    ),
  syncEnterpriseExitWorkflow: (uuid: string) =>
    requestPark<EnterpriseExitDetail>(
      `/bff/operator/enterprise-exits/${uuid}/workflow-sync`,
      { method: 'POST' },
    ),
  checkEnterpriseExit: (uuid: string) =>
    requestPark<ExitCheckResult>(
      `/bff/operator/enterprise-exits/${uuid}/check`,
      {
        method: 'GET',
      },
    ),
  completeEnterpriseExit: (uuid: string, data: CompleteEnterpriseExitRequest) =>
    requestPark<EnterpriseExitDetail>(
      `/bff/operator/enterprise-exits/${uuid}/complete`,
      { method: 'POST', data },
    ),
  executeWorkflowAction: (data: ExecuteWorkflowActionRequest) =>
    requestPark<unknown>('/bff/workflow/instances/actions', {
      method: 'POST',
      data,
    }),
  listWorkflowBindings: (businessType?: string) =>
    requestPark<WorkflowBinding[]>('/bff/workflow/bindings', {
      method: 'GET',
      params: businessType ? { businessType } : undefined,
    }),
  listLaunchableWorkflowTemplates: () =>
    requestPark<WorkflowLaunchableTemplate[]>(
      '/bff/workflow/launchable-templates',
      {
        method: 'GET',
      },
    ),
  saveWorkflowBinding: (data: SaveWorkflowBindingRequest) =>
    requestPark<WorkflowBinding>('/bff/workflow/bindings', {
      method: 'PUT',
      data,
    }),
  listEnterpriseEntries: (
    params: import('./types/enterprise-entry').ParkEnterpriseEntryQuery,
  ) =>
    requestPark<
      PageResult<import('./types/enterprise-entry').ParkEnterpriseEntry>
    >('/bff/operator/enterprise-entries', { method: 'GET', params }),
  getEnterpriseEntry: (uuid: string) =>
    requestPark<import('./types/enterprise-entry').ParkEnterpriseEntry>(
      `/bff/operator/enterprise-entries/${uuid}`,
      { method: 'GET' },
    ),
  createEnterpriseEntry: (
    data: import('./types/enterprise-entry').CreateParkEnterpriseEntryRequest,
  ) =>
    requestPark<import('./types/enterprise-entry').ParkEnterpriseEntry>(
      '/bff/operator/enterprise-entries',
      { method: 'POST', data },
    ),
  submitEnterpriseEntry: (uuid: string) =>
    requestPark<{
      mode: string;
      entry: import('./types/enterprise-entry').ParkEnterpriseEntry;
    }>(`/bff/operator/enterprise-entries/${uuid}/submit`, { method: 'POST' }),
  completeEntryItem: (
    entryUuid: string,
    itemUuid: string,
    data?: import('./types/enterprise-entry').CompleteEntryItemRequest,
  ) =>
    requestPark<import('./types/enterprise-entry').ParkEnterpriseEntryItem>(
      `/bff/operator/enterprise-entries/${entryUuid}/items/${itemUuid}/complete`,
      { method: 'POST', data: data || {} },
    ),
  cancelEntryItem: (entryUuid: string, itemUuid: string) =>
    requestPark<import('./types/enterprise-entry').ParkEnterpriseEntryItem>(
      `/bff/operator/enterprise-entries/${entryUuid}/items/${itemUuid}/cancel`,
      { method: 'POST' },
    ),
  completeEnterpriseEntry: (
    uuid: string,
    data: import('./types/enterprise-entry').CompleteParkEnterpriseEntryRequest,
  ) =>
    requestPark<import('./types/enterprise-entry').ParkEnterpriseEntry>(
      `/bff/operator/enterprise-entries/${uuid}/complete`,
      { method: 'POST', data },
    ),

  // ==================== Batch 2: 在园管理与空间变更 API ====================
  getCurrentSpaces: (parkEnterpriseUuid: string) =>
    requestPark<import('./types/park').CurrentSpacesResult>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/current-spaces`,
      { method: 'GET' },
    ),
  getSpaceHistory: (parkEnterpriseUuid: string) =>
    requestPark<import('./types/park').SpaceHistoryItem[]>(
      `/bff/operator/park-enterprises/${parkEnterpriseUuid}/space-history`,
      { method: 'GET' },
    ),
  listEnterpriseSpaceChanges: (
    params?: import('./types/park').ParkEnterpriseSpaceChangeQuery,
  ) =>
    requestPark<PageResult<import('./types/park').ParkEnterpriseSpaceChange>>(
      '/bff/operator/enterprise-space-changes',
      { method: 'GET', params },
    ),
  getEnterpriseSpaceChange: (uuid: string) =>
    requestPark<import('./types/park').ParkEnterpriseSpaceChange>(
      `/bff/operator/enterprise-space-changes/${uuid}`,
      { method: 'GET' },
    ),
  createEnterpriseSpaceChange: (
    data: import('./types/park').CreateParkEnterpriseSpaceChangeRequest,
  ) =>
    requestPark<import('./types/park').ParkEnterpriseSpaceChange>(
      '/bff/operator/enterprise-space-changes',
      { method: 'POST', data },
    ),
  submitEnterpriseSpaceChange: (uuid: string) =>
    requestPark<import('./types/park').ParkEnterpriseSpaceChange>(
      `/bff/operator/enterprise-space-changes/${uuid}/submit`,
      { method: 'POST' },
    ),
  executeEnterpriseSpaceChange: (uuid: string) =>
    requestPark<import('./types/park').ParkEnterpriseSpaceChange>(
      `/bff/operator/enterprise-space-changes/${uuid}/execute`,
      { method: 'POST' },
    ),
  // ---------- 资产管理 ----------

  listAssets: async (params?: ParkAssetQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<ParkAsset>>>('/bff/operator/assets', {
        method: 'GET',
        params,
      }),
    ),
  getAsset: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAsset>>(`/bff/operator/assets/${uuid}`, {
        method: 'GET',
      }),
    ),
  createAsset: async (data: ParkAssetSaveRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAsset>>('/bff/operator/assets', {
        method: 'POST',
        data,
      }),
    ),
  updateAsset: async (uuid: string, data: Partial<ParkAssetSaveRequest>) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAsset>>(`/bff/operator/assets/${uuid}`, {
        method: 'PATCH',
        data,
      }),
    ),
  updateAssetStatus: async (uuid: string, status: ParkAssetStatus) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkAsset>>(`/bff/operator/assets/${uuid}/status`, {
        method: 'PATCH',
        data: { status },
      }),
    ),
  deleteAsset: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(`/bff/operator/assets/${uuid}`, {
        method: 'DELETE',
      }),
    ),
  listAssetReminders: async (params?: ParkAssetReminderQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<ParkAssetReminderItem>>>(
        '/bff/operator/assets/reminders',
        { method: 'GET', params },
      ),
    ),

  // ---------- 供应商库 ----------

  listSuppliers: async (params?: ParkSupplierQuery) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<PageResult<ParkSupplier>>>('/bff/operator/suppliers', {
        method: 'GET',
        params,
      }),
    ),
  listSupplierOptions: async () =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkSupplierOption[]>>('/bff/operator/suppliers/options', {
        method: 'GET',
      }),
    ),
  getSupplier: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkSupplier>>(`/bff/operator/suppliers/${uuid}`, {
        method: 'GET',
      }),
    ),
  createSupplier: async (data: ParkSupplierSaveRequest) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkSupplier>>('/bff/operator/suppliers', {
        method: 'POST',
        data,
      }),
    ),
  updateSupplier: async (uuid: string, data: Partial<ParkSupplierSaveRequest>) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<ParkSupplier>>(`/bff/operator/suppliers/${uuid}`, {
        method: 'PATCH',
        data,
      }),
    ),
  deleteSupplier: async (uuid: string) =>
    unwrapParkResponse(
      await request<ParkApiEnvelope<{ uuid: string }>>(`/bff/operator/suppliers/${uuid}`, {
        method: 'DELETE',
      }),
    ),
};
